import { Component, ViewEncapsulation, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MattersService } from 'src/app/services/matters.service';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { PreviewDocumentService } from 'src/app/services/preview-document.service';
import { ClioService } from 'src/app/services/clio.service';

export interface FileItem {
  name: string;
  id?: string; // Add id property from actual data
  clioId?: string;
  type: 'file' | 'folder' | 'image';
  size?: string;
  lastModified?: string;
  date?: string;
  selected?: boolean;
  isDuplicate?: boolean;
  path?: string;
  filePath?: string;
  children?: FileItem[];
  expanded?: boolean;
  count?: number;
  parentFolder?: string;
  parentFolderName?: string; // Add parentFolderName property that exists in the data
  // Add content property for file preview
  content?: string;
  // New properties for tree view
  depth?: number;
  visible?: boolean;
  parentPath?: string;
  // New property for already transferred files
  alreadyTransferred?: boolean;
  // Add transferredClass property for styling
  transferredClass?: string;
  // Add accessibilityLabel property for screen reader text
  accessibilityLabel?: string;
  // Add property for showing checkmark icon
  hasCheckmark?: boolean;
  // Cache properties for folder counts to optimize UI updates
  _fileCount?: number;
  _subfolderCount?: number;
  // Additional tracking properties for more reliable folder counts
  _needsCountRefresh?: boolean;
  _lastCountRefreshTime?: number;
  // Add fullPath property for complete folder path display
  fullPath?: string;
  // Add partiallySelected property for tri-state folder selection
  partiallySelected?: boolean;
}

export interface BreadcrumbItem {
  name: string;
  folder: FileItem | null;
}

@Component({
  selector: 'app-file-transfer-v2',
  templateUrl: './file-transfer-v2.component.html',
  styleUrls: ['./file-transfer-v2.component.scss'],

  encapsulation: ViewEncapsulation.None, // Add this line to disable view encapsulation
})
export class FileTransferV2Component implements OnInit {
  label: any;
  subfolders: any;
  downloadPreview() {
    throw new Error('Method not implemented.');
  }
  clearSelection() {
    throw new Error('Method not implemented.');
  }
  deselectAllFiles() {
    throw new Error('Method not implemented.');
  }
  selectAllFiles() {
    throw new Error('Method not implemented.');
  }
  caseName: any;
  matterId: string;
  matterLocalClioFiles;

  // Add missing properties needed for the cloud function API call
  projectId: string = 'nuagedx-test'; // Default value - should be updated from environment
  userId: string;

  clioFiles: FileItem[] = [];

  // Breadcrumb navigation
  breadcrumbs: BreadcrumbItem[] = [{ name: 'Home', folder: null }];
  currentFolderContents: FileItem[] = [];

  // Selected files for transfer - initialize as empty
  selectedFiles: FileItem[] = [];

  // Destination files in NuageDx
  nuageDxFiles: FileItem[] = [];

  // Duplicated files - initialize as empty array
  duplicatedFiles: FileItem[] = [];

  // Resolution option
  resolutionOptions = [
    { value: 'skip', label: 'Skip duplicates' },
    { value: 'rename', label: 'Rename' },
    { value: 'keep', label: 'Keep both', checked: true },
  ];

  // Selected resolution option - initialize with the checked option
  selectedResolutionOption: string = 'keep';

  // Track if there are exact duplicates
  hasExactDuplicates: boolean = false;

  // Flag to toggle visibility of duplicates panel and warning icons
  showDuplicatesPanel: boolean = false;

  // Add properties for file preview
  previewFileItem: FileItem | null = null;
  showFilePreview: boolean = false;

  // Add property for destination panel visibility
  showDestinationPanel: boolean = false;

  // Add properties for search functionality
  showSearchInput: boolean = false;
  searchQuery: string = '';
  filteredFolderContents: FileItem[] = [];

  // New property to track all flattened items for tree view
  flattenedItems: FileItem[] = [];

  // New property to track the current active path
  currentActivePath: string = '';

  // Add loading state properties
  loadingFolderId: string | null = null;

  // Add transfer in progress flag
  transferInProgress: boolean = false;

  // Add properties for transfer log
  showTransferLog: boolean = false;
  transferLogItems: TransferLogItem[] = [];

  // Add flag to track when file checking process is complete
  fileCheckingComplete: boolean = false;

  // Add loading message for better UX
  fileCheckingMessage: string = 'Checking for already transferred files...';

  // Add search loading state
  isSearching: boolean = false;
  sourceObjectName = '';
  transferType: any;
  // Add flag to track when all visible files are already transferred
  allVisibleFilesTransferred: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<FileTransferV2Component>,
    private matters_$: MattersService,
    private auth_$: AuthService,
    private http: HttpClient, // Add HttpClient for API calls
    private previewDocument_$: PreviewDocumentService,
    private clio_$: ClioService,
    private cdRef: ChangeDetectorRef, // Uncommented ChangeDetectorRef injection
    @Inject(MAT_DIALOG_DATA) private data: any,
  ) {
    this.caseName = data.caseName;
    this.matterId = data.clioFolderId;
    this.transferType = this.data.type;

    // Get userId from the matters service
    this.userId = this.matters_$.auth_$.uid;
  }

  // Update the logic to exclude the 'Clio' folder and merge its items into the root level
  async ngOnInit() {
    try {
      switch (this.data.type) {
        case 1:
          // It is a MATTER
          this.label = 'Clio Matter files not in NuageDx:';
          // For MATTER type, the sourceObjectName should be the caseName
          this.sourceObjectName = this.caseName;
          console.log('ngOnInit: sourceObjectName set for MATTER type:', this.sourceObjectName);
          this.clioFiles = await this.getMatterContents(this.data.clioDocId);
          console.log('Fetching matter contents for MATTER type...', this.data.clioDocId);
          break;
        case 2:
          // It is a FOLDER
          this.label = 'Clio Folder files not in NuageDx:';
          console.log('ngOnInit: Processing FOLDER type...');
          this.clioFiles = await this.getFolderContents(this.data.clioDocId);
          console.log('ngOnInit: After getFolderContents for FOLDER type, sourceObjectName:', this.sourceObjectName);
          console.log('Fetching folder contents for FOLDER type...', this.data.clioDocId);
          break;
        case 3:
          // It is a FILE
          this.label = 'Clio File not in NuageDx:';
          console.log('ngOnInit: Processing FILE type...');
          this.clioFiles = [await this.getFileContents(this.data.clioDocId)];
          console.log('ngOnInit: After getFileContents for FILE type, sourceObjectName:', this.sourceObjectName);
          console.log('===============================================');
          console.log('this.clioFiles: ', this.clioFiles);
          console.log('===============================================');
          console.log('Fetching file contents for FILE type...', this.data.clioDocId);
          break;
        default:
      }

      console.log('this.clioFiles :', this.clioFiles);

      if (!this.clioFiles || this.clioFiles.length === 0) {
        console.error('No files or folders found in the response.');
        this.fileCheckingMessage = 'No files or folders found.';
        this.fileCheckingComplete = true; // Set to true to allow UI to proceed
        return; // Exit early if no files or folders are found
      }

      // For MATTER or FOLDER type, create a root folder with sourceObjectName
      if ((this.data.type === 1 || this.data.type === 2) && this.sourceObjectName) {
        console.log(`Creating root folder structure with name: ${this.sourceObjectName}`);
        // Store the original files to use as children
        const originalFiles = [...this.clioFiles];
        // Create the root folder structure
        this.clioFiles = this.createRootFolderStructure(originalFiles);
      }

      // Mark all items as selected by default before processing anything else
      // this.markAllItemsAsSelected(this.clioFiles);

      // Ensure sourceObjectName is correctly set to the root folder name after initial loading
      if (this.clioFiles && this.clioFiles.length > 0) {
        this.sourceObjectName = this.clioFiles[0].name;
        console.log('ngOnInit: sourceObjectName set from clioFiles[0].name:', this.sourceObjectName);
      }

      // Process NuageDX matter content
      await this.processNuageDxMatterContent(this.caseName);

      /////////////////////////////////////////////////////////////////////////////////////

      // Initialize the UI immediately without waiting for matterLocalClioFiles
      // Ensure sourceObjectName is correctly set to the root folder name after initial loading
      // This needs to be done after data loading and potential root folder creation.
      if (this.clioFiles && this.clioFiles.length > 0) {
        // The first item in clioFiles should now be the root folder if created, or the original root item.
        this.sourceObjectName = this.clioFiles[0].name;
        console.log('ngOnInit: sourceObjectName finalized after loading and structuring:', this.sourceObjectName);
      }

      this.setupUI();

      // Load NuageDx content for matching in the background
      // Use .then instead of await so we don't block the UI
      this.matters_$.getNuagedxMatterContent(this.caseName).then((response: any) => {
        if (response && response.data) {
          this.matterLocalClioFiles = response.data.files;
          console.log(
            '🚀 ~ FileTransferV2Component ~ ngOnInit ~ matterLocalClioFiles loaded:',
            this.matterLocalClioFiles,
          );

          // Now that matterLocalClioFiles is available, mark already transferred files
          this.markTransferredFiles();

          // Ensure transferred files are removed from the right panel
          // this.filterTransferredFilesFromSelected();
        }
      });
    } catch (error) {
      // Handle any errors that occur during initialization
      console.error('Error initializing file transfer:', error);

      // Show a user-friendly message to the user
      this.fileCheckingMessage = 'Error loading files. Please try again later.';
      this.fileCheckingComplete = true; // Set to true to allow UI to proceed
    }
  }

  /**
   * Helper method to mark all items as selected initially
   */
  private markAllItemsAsSelected(files: FileItem[]): void {
    if (!files || files.length === 0) return;

    files.forEach(item => {
      if (!item) return;
      // Set selected to true for all items
      item.selected = true;

      // Recursively mark children as selected
      if (item.type === 'folder' && item.children) {
        this.markAllItemsAsSelected(item.children);
      }
    });
  }

  getHTMLTitle(): string {
    return `Share to NuageDx (<span class="red-text">${this.caseName}</span>)`;
  }

  convertBytestToKB = (bytes: number): string => {
    if (bytes === 0) return '0 KB';
    const kB = bytes / 1024;
    return kB.toFixed(2) + ' KB';
  };

  checkAlreadyTransferred = (id: string): boolean => {
    // Check if the file is already transferred
    return this.nuageDxFiles.some(item => item.name === id);
  };

  /**
   * Check if a file matches any already transferred files
   * Compares the clioId with clioDocId in matterLocalClioFiles
   */
  isAlreadyTransferredByClioId(file: FileItem): boolean {
    if (!this.matterLocalClioFiles || !Array.isArray(this.matterLocalClioFiles)) {
      return false;
    }

    // If file doesn't have a clioId, it can't match
    if (!file.clioId) {
      return false;
    }

    // Check if any file in matterLocalClioFiles has a matching clioDocId
    return this.matterLocalClioFiles.some(transferredFile => transferredFile.clioDocId === file.clioId);
  }

  /**
   * Mark files that have already been transferred to NuageDx
   * by checking clioId against matterLocalClioFiles
   */
  markTransferredFiles(): void {
    if (!this.matterLocalClioFiles || !Array.isArray(this.matterLocalClioFiles)) {
      console.log('No matterLocalClioFiles available to check for transferred files');
      this.fileCheckingComplete = true;
      return;
    }

    console.log('Marking already transferred files in the list...');

    // Create a recursive function to process transferred files in the hierarchy
    const processFiles = (files: FileItem[]): void => {
      if (!files || !Array.isArray(files)) return;

      files.forEach(file => {
        // For files, check if already transferred
        if (file.type === 'file' && file.clioId) {
          // Mark file as transferred if it matches
          const isTransferred = this.isAlreadyTransferredByClioId(file);
          if (isTransferred) {
            file.alreadyTransferred = true;
            file.transferredClass = 'already-transferred';
            file.accessibilityLabel = 'Already shared';
            file.hasCheckmark = true;
            console.log(`Marked file as already transferred: ${file.name} (ID: ${file.clioId})`);
          }
        }

        // For folders, recursively process their children
        if (file.type === 'folder' && file.children && Array.isArray(file.children)) {
          processFiles(file.children);

          // If all children are transferred, mark the folder as transferred too
          if (file.children.length > 0 && this.areAllFilesTransferred(file)) {
            file.accessibilityLabel = 'Already shared';
          }
        }
      });
    };

    // Process all files in-place (don't replace the array)
    processFiles(this.clioFiles);

    // Update the flattened items to reflect these changes
    this.updateFlattenedItems();

    // Update current folder contents if we're at root level
    if (this.breadcrumbs.length === 1) {
      this.updateCurrentFolderContents();
    }

    // Filter out transferred files from the right panel
    this.filterTransferredFilesFromSelected();

    // Set fileCheckingComplete flag to true after processing is complete
    console.log('Finished marking already transferred files');
    this.fileCheckingComplete = true;

    this.fileCheckingMessage = 'File checking complete';
  }

  handleSingleFileResponse = (response: {
    code: string;
    data: {
      data: {
        id: number;
        type: string;
        name: string;
        filename: string;
        size: number;
        matter: any;
      };
    };
  }) => {
    const data = response.data.data;
    console.log('🚀 ~ FileTransferV2Component ~ data:', data);

    // Format the data.
    const mapItem = (item: any): FileItem => {
      return {
        clioId: item.id,
        name: item.name,
        type: item.type === 'Document' ? 'file' : 'folder',
        size: item.latest_document_version ? `${this.convertBytestToKB(item.latest_document_version.size)}` : '0 KB',
        date: item.latest_document_version
          ? new Date(item.latest_document_version.created_at).toLocaleDateString()
          : new Date().toLocaleDateString(),
        selected: false,
        alreadyTransferred: this.checkAlreadyTransferred(item.id),
        expanded: false,
        children: item.children ? item.children.map((child: any) => mapItem(child)) : [],
      };
    };

    return mapItem(data);

    // return data.filter(item => item.type === 'Document').map((item: any) => mapItem(item));
  };

  mappingFolderDataResponse = response => {
    const data = response.data.data.data;
    console.log('🚀 ~ FileTransferV2Component ~ data:', data);

    // Format the data.
    const mapItem = (item: any): FileItem => {
      return {
        clioId: item.id,
        name: item.name,
        type: item.type === 'Document' ? 'file' : 'folder',
        size: item.latest_document_version ? `${this.convertBytestToKB(item.latest_document_version.size)}` : '0 KB',
        date: item.latest_document_version
          ? new Date(item.latest_document_version.created_at).toLocaleDateString()
          : new Date().toLocaleDateString(),
        selected: false,
        alreadyTransferred: this.checkAlreadyTransferred(item.id),
        expanded: false,
        children: item.children ? item.children.map((child: any) => mapItem(child)) : [],
      };
    };

    // Store the subfolders in the component variable, sorted by name
    this.subfolders = data
      .filter(item => item.type === 'Folder')
      .map((item: any) => ({
        name: item.name,
        id: item.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return data.map((item: any) => mapItem(item));
    // return data.filter(item => item.type === 'Document').map((item: any) => mapItem(item));
  };

  getFolderContents(matterId: string): Promise<FileItem[]> {
    // Validate matterId
    if (!matterId) {
      console.error('Invalid matterId provided:', matterId);
      return Promise.resolve([]); // Return an empty array or handle as needed
    }
    // Fetch matter contents from the service
    return this.matters_$
      .getFolderContentsV2(matterId)
      .then((response: any) => {
        console.log('🚀 ~ FileTransferV2Component ~ response:', response);
        return this.mappingFolderDataResponse(response);
      })
      .catch(error => {
        console.error('Error fetching matter contents:', error);
      });
  }

  async getFileContents(clioFileId: string): Promise<any> {
    // Validate clioFileId
    if (!clioFileId) {
      console.error('Invalid clioFileId provided:', clioFileId);
      return Promise.resolve([]); // Return an empty array or handle as needed
    }

    // Fetch matter contents from the service
    return this.matters_$
      .getDocument(clioFileId)
      .then((response: any) => {
        if (response.code === 200) {
          return this.handleSingleFileResponse(response);
        } else {
          console.error('Error fetching matter contents:', response.data.message);
        }
      })
      .catch(error => {
        console.error('Error fetching matter contents:', error);
      });
  }

  getNuageDxMatterContents(caseName: string) {
    // Fetch files from Firestore where belongsTo matches the case name
    return this.matters_$.getNuagedxMatterContent(caseName);
  }

  getMatterContents(matterId: string): Promise<FileItem[]> {
    // Fetch matter contents from the service
    // return this.matters_$.getMatterContentV2(matterId)
    return (
      this.matters_$
        .getMatterContentV2(matterId)
        // .getMatterContentV3(matterId)
        .then((response: any) => {
          if (response.data.code === 200) {
            // Set the source object name from the response if available
            if (
              response.data.data &&
              response.data.data.data &&
              response.data.data.data[0] &&
              response.data.data.data[0].parent
            ) {
              this.sourceObjectName = response.data.data.data[0].parent.name;
            }
            // return this.mappingFolderDataResponseV2(response);
            return this.mappingFolderDataResponse(response);
          } else {
            console.error('Error fetching matter contents:', response.data.message);
          }
        })
        .catch(error => {
          // console.error('Error fetching matter contents:', error);
          this.auth_$.setToastMessage(
            'Error fetching matter contents',
            error.message,
            { type: 'error', duration: 0, position: 'top', action: 'Close' },
            () => {
              console.log('Toast closed');
              this.dialogRef.close(); // Close the dialog on error
            }, // Callback to close the toast message
          );
        })
    );
  }

  /**
   * Initialize the UI components - extracted from ngOnInit to make the code cleaner
   */
  private setupUI(): void {
    // Initialize the flattened items array first - this ensures the tree structure is built
    this.updateFlattenedItems();

    // Initialize the current folder contents with root level files
    this.updateCurrentFolderContents();

    // For MATTER or FOLDER type, ensure the root folder is expanded
    if ((this.data.type === 1 || this.data.type === 2) && this.sourceObjectName && this.clioFiles.length === 1) {
      // Ensure the root folder is expanded
      const rootFolder = this.clioFiles[0];
      if (rootFolder.type === 'folder' && rootFolder.name === this.sourceObjectName) {
        rootFolder.expanded = true;

        // Update breadcrumbs to include the root folder
        this.breadcrumbs = [
          { name: 'Home', folder: null },
          { name: this.sourceObjectName, folder: rootFolder },
        ];

        // Update current folder contents to show the root folder's children
        if (rootFolder.children) {
          this.currentFolderContents = rootFolder.children;
        }
      }
    } else {
      // Make sure only one folder is expanded initially for other cases
      this.ensureSingleExpandedFolder();
    }

    // CRITICAL: Add all files (not folders) to selectedFiles
    // this.selectAllFilesExplicitly();

    // Filter out already transferred files from selectedFiles
    this.filterTransferredFilesFromSelected();

    // Check for duplicates
    this.updateDuplicatesState();

    // Initialize selected resolution option
    this.selectedResolutionOption = this.resolutionOptions.find(opt => opt.checked)?.value || 'keep';

    // Sync folder selection states with their children
    this.updateAllFolderSelectionStates();

    // Initialize showDuplicatesPanel to false
    this.showDuplicatesPanel = false;

    // Initialize filtered contents with all contents
    this.filteredFolderContents = [...this.currentFolderContents];
  }

  /**
   * Selects all files (not folders) in the file structure
   * Skips already transferred files
   */
  private selectAllFilesExplicitly(): void {
    console.log('Explicitly selecting all files in all data structures');

    // Process files recursively
    const processFiles = (items: FileItem[], parentFolder: string | null = null) => {
      items.forEach(item => {
        // Skip already transferred items
        if (this.isFileAlreadyTransferred(item)) return;

        // Set selection state to true
        item.selected = true;

        if (item.type === 'file' || item.type === 'image') {
          // Add files to selectedFiles
          this.addFileToSelectedList(item, parentFolder);
        } else if (item.type === 'folder' && item.children) {
          // For folders, process children recursively
          processFiles(item.children, item.name);
        }
      });
    };

    // Start processing from root items
    processFiles(this.clioFiles);

    // Update the flattened items to reflect the selection state
    this.flattenedItems.forEach(item => {
      if (!this.isFileAlreadyTransferred(item)) {
        item.selected = true;
      }
    });
  }

  /**
   * Process NuageDX matter content and organize it into a proper hierarchy
   * @param caseName The name of the case/matter
   * @returns Promise that resolves when processing is complete
   */
  async processNuageDxMatterContent(caseName: string): Promise<void> {
    try {
      const response = await this.getNuageDxMatterContents(caseName);
      const files = response['data']['files'].filter(p => p.clioDocId !== null);

      // Filter out the 'Clio' folder and merge its children into the root level
      let filteredFiles = files.filter(item => item.name !== 'Clio');

      // Stopping here to see if we can get the folder structure to work.
      // filteredFiles = filteredFiles.filter(item => item.type !== 'folder');

      const clioFolder = files.find(item => item.name === 'Clio');
      if (clioFolder && clioFolder.children) {
        filteredFiles.push(...clioFolder.children);
      }

      // Ensure proper folder and file distinction
      // First ensure all folders have a children array
      filteredFiles = filteredFiles.map(item => {
        if (item.type === 'folder') {
          item.children = item.children || [];
        }
        return item;
      });

      // Organize the files into a proper hierarchy based on parentFolder property
      this.nuageDxFiles = this.organizeFileHierarchy(filteredFiles);

      // Then sort the items to show folders at the top
      this.nuageDxFiles.sort((a, b) => {
        // Folders come before files
        if (a.type === 'folder' && b.type !== 'folder') return -1;
        if (a.type !== 'folder' && b.type === 'folder') return 1;

        // Within the same type, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });

      // Update the UI
      this.updateFlattenedItems();
    } catch (error) {
      console.error('Error processing NuageDX matter content:', error);
      throw error; // Rethrow to allow calling code to handle the error
    }
  }

  async openClioFilePreview(file): Promise<void> {
    const clioFileId = file.clioId;
    // Look at this url: https://app.clio.com/nc/#/documents/18427181620/details
    // Open a new window with this url
    window.open(`https://app.clio.com/nc/#/documents/${clioFileId}/details`, '_blank');
  }

  backToClio(clioId): void {
    console.log('Back to Clio:', clioId);
    // this.openClioFilePreview(clioId);
  }

  openNuageDxFilePreview(file): void {
    // console.log('Opening NuageDX file preview:', file);
    // this.previewDocument_$.simpleFileClick(file.name, file.id, file.storageBucket, file.filePath);

    this.previewDocument_$.noZipFileClick(file.filePath, file.name, this.auth_$);
  }

  // Add method for opening file preview
  // openFilePreview(file: FileItem): void {
  //   if (file.type === 'folder') return;

  //   // Set mock content based on file type
  //   if (!file.content) {
  //     if (file.type === 'image') {
  //       file.content =
  //         'This is an image preview placeholder. In a real application, an actual image would be displayed here.';
  //     } else {
  //       file.content = `This is the content of ${file.name}. In a real application, the actual file content would be retrieved and displayed here.`;
  //     }
  //   }

  //   this.previewFileItem = file;
  //   this.showFilePreview = true;
  //   console.log(`Opened preview for file: ${file.name}`);
  // }

  // Add method for closing file preview
  closeFilePreview(): void {
    this.showFilePreview = false;
    this.previewFileItem = null;
  }

  /**
   * Updates the contents of the current folder based on breadcrumb path
   * Filters out already transferred files
   */
  updateCurrentFolderContents(): void {
    // Start with the root level files
    let contents: FileItem[] = [...this.clioFiles];

    // If we're not at the root, navigate to the current folder
    if (this.breadcrumbs.length > 1) {
      for (let i = 1; i < this.breadcrumbs.length; i++) {
        const breadcrumb = this.breadcrumbs[i];
        if (breadcrumb.folder && breadcrumb.folder.children) {
          contents = [...breadcrumb.folder.children];
        }
      }
    }

    // Sort contents with folders first before assigning
    this.currentFolderContents = this.sortItemsFoldersFirst(contents);

    // Update filtered contents as well (will respect any active search)
    this.filterFiles();

    // Sync selection states whenever contents are updated
    this.syncSelectionStatesAfterNavigation();

    // Check if all files are already transferred
    this.checkIfAllFilesTransferred();
  }

  /**
   * Check if all items in the current view are already transferred
   * This is used to display a message when all files are already transferred
   */
  checkIfAllFilesTransferred(): void {
    // Count all files in the current folder (including transferred ones)
    const totalItems = this.currentFolderContents.length;

    // If there are no files in the current view, consider all transferred
    if (totalItems === 0) {
      this.allVisibleFilesTransferred = false;
      return;
    }

    // Count how many items are already transferred
    const transferredItems = this.currentFolderContents.filter(item => this.isFileAlreadyTransferred(item)).length;

    // All files are transferred if the count matches the total
    this.allVisibleFilesTransferred = transferredItems === totalItems;

    console.log(
      `All files transferred check: ${this.allVisibleFilesTransferred} (${transferredItems}/${totalItems} transferred items)`,
    );
  }

  /**
   * Navigate to a specific folder
   * Enhanced to handle empty folders better
   */
  async navigateToFolder(folder: FileItem): Promise<void> {
    // Set loading state for this folder
    this.loadingFolderId = folder.name;

    try {
      // Fetch the folder contents
      const contents = await this.getFolderContents(folder.id);

      // Check if the folder has children or if all files are already transferred
      if (!contents || contents.length === 0) {
        // Empty folder - keep it closed and mark as already synced
        folder.expanded = false; // Keep the folder closed

        // Also update any references to this folder in all data structures
        // Update the actual folder in the original data structure
        const sourceFolder = this.findFolderInSource(folder.name, folder.parentPath || '');
        if (sourceFolder) {
          sourceFolder.expanded = false;
        }

        // Update in flattened items to ensure visual consistency
        this.flattenedItems.forEach(item => {
          if (item.type === 'folder' && item.name === folder.name && item.parentPath === folder.parentPath) {
            item.expanded = false;
          }
        });

        // Force an update to the class bindings in the UI
        this.updateItemVisibility();

        // If we have any open folder references in the breadcrumbs, update them
        this.breadcrumbs.forEach(crumb => {
          if (crumb.folder && crumb.folder.name === folder.name) {
            crumb.folder.expanded = false;
          }
        });

        console.log(`Folder ${folder.name} is empty or fully synced, keeping it closed`);
        return; // Important - exit early to prevent further processing
      } else {
        // Has content - proceed with normal folder opening
        folder.expanded = true;
        folder.children = contents;

        // Check if all files in this folder are already transferred

        // Update current folder contents if this is the active folder
        this.updateBreadcrumbsForFolder(folder);
        this.currentFolderContents = folder.children;
        this.filterFiles();

        // Check if all files are already transferred
        this.checkIfAllFilesTransferred();
      }

      // Update the flattened items and visibility
      this.updateFlattenedItems();
      this.updateItemVisibility();

      // Ensure selection states remain synchronized
      this.syncSelectionStatesAfterNavigation();

      console.log(`Navigated to folder: ${folder.name}, has content: ${contents && contents.length > 0}`);
    } catch (error) {
      console.error(`Error navigating to folder ${folder.name}:`, error);
      // In case of error, keep the folder closed
      folder.expanded = false;

      // Ensure UI consistency by updating all references
      const sourceFolder = this.findFolderInSource(folder.name, folder.parentPath || '');
      if (sourceFolder) {
        sourceFolder.expanded = false;
      }
    } finally {
      // Clear loading state
      this.loadingFolderId = null;
    }
  }

  /**
   * Navigate to a specific breadcrumb
   */
  async navigateToBreadcrumb(index: number): Promise<void> {
    if (index >= this.breadcrumbs.length) return;

    // Get the target breadcrumb
    const targetBreadcrumb = this.breadcrumbs[index];

    try {
      // Set loading state if navigating to a folder (not root)
      if (index > 0 && targetBreadcrumb.folder) {
        this.loadingFolderId = targetBreadcrumb.folder.name;

        // Wait for the async operation to complete
        await this.fetchFolderContents(targetBreadcrumb.folder);
      }

      // Collapse all folders
      this.flattenedItems.forEach(item => {
        if (item.type === 'folder') {
          item.expanded = false;
        }
      });

      // If this is not the root, expand the target folder
      if (index > 0 && targetBreadcrumb.folder) {
        targetBreadcrumb.folder.expanded = true;

        // Also expand all parent folders
        this.expandParentFolders(targetBreadcrumb.folder);
      }

      // Update visibility
      this.updateItemVisibility();

      // Remove breadcrumbs after the clicked one
      this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);

      // Update current folder contents
      if (index === 0) {
        // Root level
        this.currentFolderContents = this.clioFiles;
      } else if (targetBreadcrumb.folder && targetBreadcrumb.folder.children) {
        // Specific folder
        this.currentFolderContents = targetBreadcrumb.folder.children;
      }

      // Update filtered contents
      this.filterFiles();

      // Check if all files are already transferred
      this.checkIfAllFilesTransferred();

      console.log(`Navigated to breadcrumb: ${targetBreadcrumb.name}`, {
        index,
        breadcrumbs: this.breadcrumbs,
        currentContents: this.currentFolderContents.length,
      });

      // Make sure to sync selection states after navigation
      this.syncSelectionStatesAfterNavigation();
    } catch (error) {
      console.error(`Error navigating to breadcrumb:`, error);
    } finally {
      // Clear loading state
      this.loadingFolderId = null;
    }
  }

  /**
   * Get the currently active folder (last folder in breadcrumbs)
   */
  getCurrentFolder(): FileItem | null {
    if (this.breadcrumbs.length <= 1) return null;
    return this.breadcrumbs[this.breadcrumbs.length - 1].folder;
  }

  /**
   * Toggles the visibility of the duplicates panel and warning icons
   */
  toggleShowDuplicates(): void {
    this.showDuplicatesPanel = !this.showDuplicatesPanel;
    console.log(`Duplicates panel visibility: ${this.showDuplicatesPanel}`);
  }

  /**
   * Toggles folder expansion state
   * Maintains proper folder hierarchy visibility
   */
  async toggleFolder(folder: FileItem): Promise<void> {
    console.log('toggleFolder called for:', folder.name, 'Expanded state:', folder.expanded);
    // Prevent toggling the root folder when it's the sourceObjectName folder
    if (folder.name === this.sourceObjectName && folder.depth === 0) {
      // Added depth check for safety
      console.log('Attempted to toggle root folder, prevented.');
      return;
    }

    if (folder.expanded) {
      console.log('Collapsing folder:', folder.name);
      // If we're collapsing, no need for loading state or async operation
      // Explicitly close the folder (visual state)
      folder.expanded = false;
      console.log('Folder expanded state set to false:', folder.name);

      // Important: Update any other references to this folder to ensure consistent UI
      const sourceFolder = this.findFolderInSource(folder.name, folder.parentPath || '');
      if (sourceFolder) {
        sourceFolder.expanded = false;
        console.log('Source folder expanded state set to false:', sourceFolder.name);
      }

      // Also update in flattened items to ensure visual consistency
      this.flattenedItems.forEach(item => {
        if (item.type === 'folder' && item.name === folder.name && item.parentPath === folder.parentPath) {
          item.expanded = false;
        }
      });

      this.updateItemVisibility();
      console.log('updateItemVisibility called.');

      // After updating visibility, ensure selection states are preserved
      this.syncSelectionStatesAfterNavigation();
      console.log('syncSelectionStatesAfterNavigation called.');

      // Ensure folder selection state is preserved visually
      this.refreshAllFolderReferences();
      console.log('refreshAllFolderReferences called.');

      // Explicitly trigger change detection to update the view
      this.cdRef.detectChanges();
      console.log('Change detection triggered after collapse.');

      return;
    }

    // This is the block for EXPANDING a folder
    this.loadingFolderId = folder.name;

    try {
      const accessibilityLabelMap = new Map<string, string>();
      this.flattenedItems.forEach(item => {
        if (item.type === 'folder') {
          const key = `${item.name}|${item.parentPath || ''}`;
          accessibilityLabelMap.set(key, item.accessibilityLabel || '');
        }
      });

      // CHANGE HERE: Use closeAllFolders instead of closeSiblingFolders
      // This ensures only ONE folder is expanded at a time in the entire tree
      // this.closeAllFolders(folder);

      // Call closeSiblingFolders to collapse all siblings before expanding the target folder
      // this.closeSiblingFolders(folder);

      // NOTE: Removing the early call to closeSiblingFoldersDirectly 
      // Will call it after folder expansion but before updateFlattenedItems

      const folderContents = await this.fetchFolderContents(folder);

      if (!folderContents || folderContents.length === 0) {
        folder.expanded = false;
        const sourceFolder = this.findFolderInSource(folder.name, folder.parentPath || '');
        if (sourceFolder) {
          sourceFolder.expanded = false;
        }
        this.flattenedItems.forEach(item => {
          if (item.type === 'folder' && item.name === folder.name && item.parentPath === folder.parentPath) {
            item.expanded = false;
          }
        });
        this.updateItemVisibility();
        console.log(`Folder ${folder.name} is empty or fully synced, keeping it closed`);
        this.loadingFolderId = null; // Clear loading state here
        return;
      }

      // IMPORTANT: Update the actual folder in the original data structure
      if (folder.parentPath) {
        const parentFolderPath = folder.parentPath.split('/');
        const parentFolderName = parentFolderPath[parentFolderPath.length - 1];
        const parentFolder = this.findFolderInSource(parentFolderName, parentFolderPath.slice(0, -1).join('/'));
        if (parentFolder && parentFolder.children) {
          const actualFolder = parentFolder.children.find(child => child.name === folder.name);
          if (actualFolder) {
            actualFolder.children = folderContents;
            actualFolder.expanded = true;
            console.log(`Updated source folder ${folder.name} with ${folderContents.length} children`);
          }
        }
      } else {
        // Root level folder
        const actualFolder = this.clioFiles.find(item => item.name === folder.name);
        if (actualFolder) {
          actualFolder.children = folderContents;
          actualFolder.expanded = true;
          console.log(`Updated root folder ${folder.name} with ${folderContents.length} children`);
        }
      }

      // Now set the expanded state on the folder object itself
      folder.expanded = true;
      folder.children = folderContents;

      // CRITICAL: Close sibling folders AFTER expansion but BEFORE updateFlattenedItems
      // This ensures they stay closed when the flattened view is rebuilt
      this.closeSiblingFoldersDirectly(folder);

      // Mark all files as transferred if they're already transferred
      // This happens after loading children
      this.markTransferredInContents(folderContents);

      // If the folder being expanded is already selected (e.g., its checkbox was checked),
      // ensure all its newly loaded children are also marked as selected.
      if (folder.selected) {
        console.log(`Folder ${folder.name} is selected and was expanded. Ensuring its children are selected.`);
        // The 'folder' object here is from flattenedItems and now has its .children populated.
        // updateChildrenSelectionState will recursively select children and add items to selectedFiles.
        this.updateChildrenSelectionState(folder, true);

        // Ensure the folder itself is correctly represented in selectedFiles.
        // It should have been added when its checkbox was ticked, but this is a safeguard.
        const parentNameForSelectedFiles = folder.parentPath ? folder.parentPath.split('/').pop() || 'Root' : 'Root';
        const isFolderItselfInSelectedList = this.selectedFiles.some(
          sf =>
            sf.name === folder.name &&
            sf.type === 'folder' &&
            (sf.parentFolder || 'Root') === parentNameForSelectedFiles &&
            (!folder.clioId || sf.clioId === folder.clioId), // Match clioId if available for uniqueness
        );

        if (!isFolderItselfInSelectedList) {
          const folderToAdd = {
            ...folder, // Use the 'folder' from flattenedItems which has correct parentPath
            selected: true,
            parentFolder: parentNameForSelectedFiles, // Set parentFolder for selectedFiles context
          };
          this.selectedFiles.push(folderToAdd);
          console.log(
            `Added expanded selected folder ${folder.name} (path: ${folder.parentPath}) to selectedFiles with parentFolder: ${parentNameForSelectedFiles}.`,
          );
        }

        // After selecting children, the folder's display in the selected files list (right panel)
        // might need an update for counts.
        this.forceUpdateFolderInSelectedFiles(folder.name);
      }

      if (folderContents.length === 0 || this.areAllFilesTransferred(folder)) {
        folder.accessibilityLabel = 'Already shared';
      } else {
        folder.accessibilityLabel = '';
      }

      this.updateBreadcrumbsForFolder(folder);
      this.updateFlattenedItems(); // CRITICAL: Rebuild flattened items

      this.flattenedItems.forEach(item => {
        if (item.type === 'folder') {
          const key = `${item.name}|${item.parentPath || ''}`;
          if (accessibilityLabelMap.has(key) && accessibilityLabelMap.get(key)) {
            item.accessibilityLabel = accessibilityLabelMap.get(key) || '';
          }
        }
      });

      this.updateItemVisibility();
      console.log(`Toggled folder ${folder.name} expansion: ${folder.expanded}`);

      // Log root item properties and sourceObjectName after updates in toggleFolder (expansion)
      if (this.clioFiles && this.clioFiles.length > 0) {
        const rootItem = this.clioFiles[0];
        console.log('toggleFolder - Root Item Check After Expand Updates:', {
          name: rootItem.name,
          depth: rootItem.depth,
          sourceObjectName: this.sourceObjectName,
        });
      }

      const isCurrentFolder = this.breadcrumbs.findIndex(b => b.folder === folder) === this.breadcrumbs.length - 1;
      if (isCurrentFolder) {
        this.currentFolderContents = folder.children || [];
        this.filterFiles();
      }

      this.syncSelectionStatesAfterNavigation();
    } catch (error) {
      console.error(`Error toggling folder ${folder.name}:`, error);
      folder.expanded = false;
      const sourceFolder = this.findFolderInSource(folder.name, folder.parentPath || '');
      if (sourceFolder) {
        sourceFolder.expanded = false;
      }
      this.updateItemVisibility();
    } finally {
      this.loadingFolderId = null;
    }
  }

  /**
   * Toggle folder selection and selects/deselects all child files
   * Modified to properly update child selection states in the UI
   * and prevent adding children to selectedFiles array when folder is selected
   */
  toggleFolderSelection(folder: FileItem, newSelectionState: boolean): void {
    // The newSelectionState parameter now contains the new checkbox state from ngModelChange
    console.log(`Folder ${folder.name} selection changed to: ${newSelectionState}`);

    // IMPORTANT: Explicitly set the folder's selected state
    folder.selected = newSelectionState;

    // Special handling for the root folder (sourceObjectName)
    if (folder.name === this.sourceObjectName && (this.data.type === 1 || this.data.type === 2)) {
      // For the root folder, we don't add it to selectedFiles
      // Instead, we directly manage its children
      if (folder.children) {
        folder.children.forEach(child => {
          // Update child's selection state
          if (!this.isFileAlreadyTransferred(child)) {
            child.selected = newSelectionState;

            // For files, add/remove them directly
            if (child.type === 'file' || child.type === 'image') {
              if (newSelectionState) {
                this.addFileToSelectedList(child, this.sourceObjectName);
              } else {
                this.selectedFiles = this.selectedFiles.filter(
                  item => !(item.name === child.name && item.parentFolder === this.sourceObjectName),
                );
              }
            }
            // For folders, process their children recursively
            else if (child.type === 'folder') {
              // Process this folder's children recursively
              this.updateChildrenSelectionState(child, newSelectionState);
            }
          }
        });
      }

      // Update UI state
      this.updateFlattenedItems();
      this.updateItemVisibility();

      // Update duplicates state
      this.updateDuplicatesState();

      return;
    }

    // Regular folder handling (not the root folder)
    // For folders, we don't add the folder itself to selectedFiles
    // Instead, we add all its child files directly
    if (newSelectionState) {
      // Add all child files to selectedFiles
      if (folder.children) {
        this.addFolderChildrenToSelectedFiles(folder);
      }
    } else {
      // Remove all child files from selectedFiles
      this.removeFolderChildrenFromSelectedFiles(folder);
    }

    // Find the actual folder in the source data structure to ensure UI consistency
    const actualFolder = this.findFolderInSource(folder.name, folder.parentPath || '');

    // Skip processing if no children or folder not found
    if (!actualFolder || !actualFolder.children || actualFolder.children.length === 0) return;

    // Update selection state on the actual folder
    actualFolder.selected = newSelectionState;

    // CRITICAL: Update the selection state of all children in the source data structure
    this.updateChildSelectionInSourceData(actualFolder, newSelectionState);

    // Update the selection state of all children in the flattened items array
    this.updateChildrenInFlattenedItems(folder, newSelectionState);

    // IMPORTANT: Force a refresh of the UI to ensure all changes are visible
    setTimeout(() => {
      // Update the flattened items to reflect the changes in the UI
      this.updateFlattenedItems();
      this.updateItemVisibility();

      // Update duplicates state
      this.updateDuplicatesState();

      // Force Angular to detect changes
      this.refreshAllFolderReferences();
    }, 0);
  }

  /**
   * Updates the selection state of all children in the source data structure
   * This ensures that when a folder is unchecked, all its children are also unchecked
   */
  private updateChildSelectionInSourceData(folder: FileItem, selected: boolean): void {
    if (!folder.children) return;

    folder.children.forEach(child => {
      // Skip already transferred files
      if (this.isFileAlreadyTransferred(child)) return;

      // Update the child's selection state
      child.selected = selected;

      // If this is a folder, recursively update its children
      if (child.type === 'folder' && child.children) {
        this.updateChildSelectionInSourceData(child, selected);
      }

      // Update selectedFiles array based on selection state
      if (selected) {
        // Add to selectedFiles if not already there
        if (child.type === 'file' || child.type === 'image') {
          this.addFileToSelectedList(child, folder.name);
        } else if (child.type === 'folder') {
          const selectedFolder = {
            ...child,
            selected: true,
            parentFolder: folder.name,
          };

          // Check if already in the list
          if (
            !this.selectedFiles.some(
              f => f.name === child.name && f.type === 'folder' && (f.parentFolder || '') === folder.name,
            )
          ) {
            this.selectedFiles.push(selectedFolder);
          }
        }
      } else {
        // Remove from selectedFiles
        this.selectedFiles = this.selectedFiles.filter(
          item => !(item.name === child.name && (item.parentFolder || '') === folder.name),
        );
      }
    });
  }

  /**
   * Updates only the UI state of children without adding them to selectedFiles
   */
  updateChildrenUIState(folder: FileItem, selected: boolean): void {
    if (!folder.children) return;

    folder.children.forEach(child => {
      // Update child's selection state
      child.selected = selected;

      // If deselecting, remove any child files from selectedFiles
      if (!selected) {
        if (child.type === 'file' || child.type === 'image') {
          // Remove from selected files
          this.selectedFiles = this.selectedFiles.filter(
            item => !(item.name === child.name && item.parentFolder === folder.name),
          );
        }
      }

      // Recursively process nested folders
      if (child.type === 'folder' && child.children) {
        this.updateChildrenUIState(child, selected);
      }
    });

    // Always update the folder in the selected files list to ensure counts are correct
    if (selected) {
      this.forceUpdateFolderInSelectedFiles(folder.name);
    }
  }

  /**
   * Removes all files from a specific folder from the selectedFiles array
   */
  removeSelectedFilesFromFolder(folder: FileItem): void {
    if (!folder.children) return;
    // Get the folder name for identification
    const folderName = folder.name;
    // Remove all files from this folder
    this.selectedFiles = this.selectedFiles.filter(item => item.parentFolder !== folderName);
    // Update duplicates state
    this.updateDuplicatesState();
  }

  /**
   * Check if all files in a folder are selected
   */
  areAllFolderFilesSelected(folder: FileItem): boolean {
    if (!folder.children || folder.children.length === 0) return false;
    return folder.children.every(child => child.selected);
  }

  /**
   * Check if any files in a folder are selected (including in nested subfolders)
   */
  areSomeFolderFilesSelected(folder: FileItem): boolean {
    if (!folder.children || folder.children.length === 0) return false;

    // First check direct children
    const directChildrenStatus =
      folder.children.some(child => child.selected) && !folder.children.every(child => child.selected);

    if (directChildrenStatus) return true;

    // If direct children don't have mixed selection state, recursively check nested folders
    for (const child of folder.children) {
      if (child.type === 'folder') {
        // Either child folder is selected or it has some selected files
        if (child.selected) return true;

        // Check if any descendant items of this child folder are selected
        const hasSelectedDescendants = this.folderHasSelectedDescendants(child);
        if (hasSelectedDescendants) return true;
      }
    }

    return false;
  }

  /**
   * Helper function to recursively check if a folder has any selected descendants
   * This ensures even deeply nested selections are detected for proper indeterminate state
   */
  folderHasSelectedDescendants(folder: FileItem): boolean {
    if (!folder.children || folder.children.length === 0) return false;

    // Check if any direct children are selected
    if (folder.children.some(child => child.selected)) return true;

    // Recursively check any subfolder children
    for (const child of folder.children) {
      if (child.type === 'folder' && this.folderHasSelectedDescendants(child)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Collapses all folders in the clioFiles list
   */
  collapseAllFolders(): void {
    this.collapseAllFoldersInList(this.clioFiles);
    this.collapseAllFoldersInList(this.nuageDxFiles);
  }

  /**
   * Collapses all folders in a given file list
   */
  collapseAllFoldersInList(fileList: FileItem[]): void {
    fileList.forEach(item => {
      if (item.type === 'folder') {
        item.expanded = false;

        // Preserve the "Already synced" label if it exists
        if (item.accessibilityLabel === 'Already synced') {
          item.accessibilityLabel = 'Already synced';
        }

        // Recursively collapse nested folders if any
        if (item.children) {
          this.collapseAllFoldersInList(item.children.filter(child => child.type === 'folder') as FileItem[]);
        }
      }
    });
  }

  /**
   * Ensures only a single folder is expanded initially
   */
  ensureSingleExpandedFolder(): void {
    let foundExpanded = false;
    // Process clioFiles
    for (let i = 0; i < this.clioFiles.length; i++) {
      const item = this.clioFiles[i];
      if (item.type === 'folder') {
        // Skip the root folder if it's the sourceObjectName folder
        if (item.name === this.sourceObjectName) {
          continue;
        }

        if (item.expanded && !foundExpanded) {
          foundExpanded = true;
        } else {
          item.expanded = false;
          // Preserve the "Already synced" label if it exists
          if (item.accessibilityLabel === 'Already synced') {
            item.accessibilityLabel = 'Already synced';
          }
        }
      }
    }
    // Process nuageDxFiles only if no expanded folder was found in clioFiles
    if (!foundExpanded) {
      for (let i = 0; i < this.nuageDxFiles.length; i++) {
        const item = this.nuageDxFiles[i];
        if (item.type === 'folder') {
          // Skip the root folder if it's the sourceObjectName folder
          if (item.name === this.sourceObjectName) {
            continue;
          }

          if (item.expanded && !foundExpanded) {
            foundExpanded = true;
          } else {
            item.expanded = false;
            // Preserve the "Already synced" label if it exists
            if (item.accessibilityLabel === 'Already synced') {
              item.accessibilityLabel = 'Already synced';
            }
          }
        }
      }
    }
  }

  /**
   * Select all files in the current view
   * Modified to work with breadcrumb navigation and respect alreadyTransferred property
   */
  selectAll(): void {
    const currentFolder = this.getCurrentFolder();

    if (currentFolder) {
      // We're in a subfolder, select all files in this folder
      // Always set selection state to true when called from setupUI
      const newSelectionState = true;

      // Update folder selection state
      currentFolder.selected = newSelectionState;

      // Update all children
      if (currentFolder.children) {
        // Update selection state for children
        currentFolder.children.forEach(child => {
          // Only update selection state if not already transferred
          if (!this.isFileAlreadyTransferred(child)) {
            child.selected = newSelectionState;

            // Add to selectedFiles list
            if (newSelectionState) {
              this.addFileToSelectedList(child, currentFolder.name);
            }
          }
        });
      }
    } else {
      // We're at the root level
      // When called from setupUI, always select all items
      const newSelectionState = true;

      // Update selection for current view items (both files and folders)
      this.currentFolderContents.forEach(item => {
        if (!this.isFileAlreadyTransferred(item)) {
          // Set selected state for both files and folders
          item.selected = newSelectionState;

          if (newSelectionState) {
            // Add to selectedFiles regardless of type (file or folder)
            this.addFileToSelectedList(item, null);

            // If it's a folder, also select its children recursively
            if (item.type === 'folder' && item.children) {
              this.updateChildrenSelectionState(item, true);
              this.updateChildrenInFlattenedItems(item, true);
            }
          }
        }
      });
    }

    // Update duplicates state
    this.updateDuplicatesState();

    // Update all folder selection states
    this.updateAllFolderSelectionStates();

    // Refresh the UI representation
    this.updateFlattenedItems();
  }

  /**
   * Helper method to add a file to the selected files list
   * Modified to be more robust for files from nested folders and prevent
   * adding files that are already covered by a selected parent folder
   * Also prevents adding already transferred files
   */
  private addFileToSelectedList(file: FileItem, parentFolderName: string | null): void {
    // Don't add already transferred files
    if (this.isFileAlreadyTransferred(file)) {
      return;
    }

    // Check if the parent folder is already selected - if so, don't add the file individually
    const isParentFolderSelected = this.selectedFiles.some(
      item => item.type === 'folder' && item.name === parentFolderName && !item.parentFolder,
    );

    if (isParentFolderSelected) {
      // Don't add individual file if parent folder is already selected
      // But make sure the file's UI state is updated
      file.selected = true;
      return;
    }

    // Determine the parent folder name and full path
    const actualParentFolder =
      parentFolderName || (this.breadcrumbs.length > 1 ? this.breadcrumbs[this.breadcrumbs.length - 1].name : 'Root');

    // Build the complete path from breadcrumbs or file's parent path
    const fullPath = this.getFullPathForFile(file, actualParentFolder);

    // Check if file is already in the selected list
    const existingFile = this.selectedFiles.find(
      item => item.name === file.name && item.parentFolder === actualParentFolder && item.type === file.type,
    );

    if (!existingFile) {
      // Create a copy with additional info - ENSURE parentFolder is set to a meaningful value
      const selectedFile = {
        ...file,
        size: file.size || '0KB',
        date: file.date || this.getCurrentDate(),
        isDuplicate: this.checkIfDuplicate(file.name),
        parentFolder: actualParentFolder, // Immediate parent folder name
        fullPath: fullPath, // Store the complete path hierarchy
        parentPath: file.parentPath || this.findCompletePath(file), // Ensure parentPath is set
        selected: true, // IMPORTANT: Ensure the file is marked as selected
      };
      this.selectedFiles.push(selectedFile);
      console.log(`Added file to selected list: ${selectedFile.name} from ${selectedFile.fullPath}`);
    } else {
      // If the file already exists in the selected list, make sure it's marked as selected
      existingFile.selected = true;
    }

    // Also ensure the original file is marked as selected
    file.selected = true;
  }

  /**
   * Gets the full path for a file based on its location in the hierarchy
   * @param file The file to get the path for
   * @param immediateParent The immediate parent folder name
   * @returns The full path as a string (e.g., "Root/Folder1/Folder2")
   */
  getFullPathForFile(file: FileItem, immediateParent: string): string {
    // Start with "Root"
    let path = 'Root';

    // If we have a file with parentPath property, use that
    if (file.parentPath) {
      return `Root/${file.parentPath}`;
    }

    // Try to recursively find the complete path by traversing the file structure
    const completePath = this.findCompletePath(file);
    if (completePath) {
      return `Root/${completePath}`;
    }

    // If we're currently inside a folder hierarchy (breadcrumbs has more than "Home")
    if (this.breadcrumbs.length > 1) {
      // Build path from breadcrumbs (skip "Home" at index 0)
      path = this.breadcrumbs
        .slice(1)
        .map(crumb => crumb.name)
        .join('/');

      return `Root/${path}`;
    }

    // Fallback: if immediateParent is not "Root", add it
    if (immediateParent && immediateParent !== 'Root') {
      return `Root/${immediateParent}`;
    }

    return path;
  }

  /**
   * Recursively finds the complete path for a file by traversing the file structure
   * @param file The file to find the path for
   * @returns The complete path as a string, or null if not found
   */
  findCompletePath(file: FileItem): string | null {
    // Helper function to recursively search for a file in the file structure
    const findFile = (files: FileItem[], currentPath: string = ''): string | null => {
      for (const item of files) {
        if (item.type === 'folder' && item.children) {
          // Construct the path for this level
          const nextPath = currentPath ? `${currentPath}/${item.name}` : item.name;

          // Check if the file is a direct child of this folder
          const foundChild = item.children.find(
            child =>
              child.name === file.name &&
              child.type === file.type &&
              child.size === file.size &&
              child.date === file.date,
          );

          if (foundChild) {
            return nextPath; // Found the parent folder, return its path
          }

          // If not found at this level, search deeper in the tree
          const deeperPath = findFile(item.children.filter(child => child.type === 'folder') as FileItem[], nextPath);
          if (deeperPath) {
            return deeperPath; // Found in a deeper level
          }
        }
      }
      return null; // Not found in this branch
    };

    return findFile(this.clioFiles);
  }

  /**
   * Toggle file selection
   * Modified to handle files with the same name from different folders
   * and respect alreadyTransferred flag and parent folder selection state
   */
  toggleFileSelection(file: FileItem): void {
    // Don't allow selection of already transferred files
    if (file.alreadyTransferred) {
      file.selected = false;
      return;
    }

    // Find the parent folder for proper identification - do this regardless of selection state
    const parentFolder = this.findParentFolder(this.clioFiles, file);
    // Get the complete path for this file based on breadcrumbs or traversing the file structure
    const parentFolderName = parentFolder
      ? parentFolder.name
      : this.breadcrumbs.length > 1
        ? this.breadcrumbs[this.breadcrumbs.length - 1].name
        : 'Root';

    // Check if the parent folder is already selected
    const isParentSelected = this.selectedFiles.some(
      item => item.type === 'folder' && item.name === parentFolderName && !item.parentFolder,
    );

    // When parent folder is selected, don't add/remove individual files to the selectedFiles list
    // Just update the UI state
    if (isParentSelected) {
      // When the parent folder is selected, the file selection state only affects UI
      // We don't need to modify the selectedFiles array
      console.log(
        `File ${file.name} selection toggled, but parent folder is already selected. Only updating UI state.`,
      );

      // Update parent folder's selection state and counts if necessary
      if (parentFolder) {
        this.updateFolderSelectionState(parentFolder);
        this.forceUpdateParentFolderInSelectedFiles(parentFolderName);
      }

      return;
    }

    // Get the full path for the file by traversing up the folder structure
    const filePath = this.findCompletePath(file);
    const currentLocation = this.breadcrumbs.length > 1 ? this.breadcrumbs[this.breadcrumbs.length - 1].name : 'Root';

    // Normal handling when parent folder is NOT selected
    if (file.selected) {
      // If file is selected, add it to the selectedFiles array if not already there
      // Create a copy with additional info to track its origin
      const selectedFile = {
        ...file,
        size: file.size || '0KB',
        date: file.date || this.getCurrentDate(),
        isDuplicate: this.checkIfDuplicate(file.name),
        parentFolder: parentFolderName, // Immediate parent folder name
        fullPath: filePath ? `Root/${filePath}` : `Root/${parentFolderName === 'Root' ? '' : parentFolderName}`, // Complete path
        parentPath: filePath, // Store the found path for future reference
        // Add location information to clearly identify where this file was selected from
        locationInfo: {
          fromRoot: parentFolderName === 'Root',
          currentView: currentLocation,
          breadcrumbPath: this.breadcrumbs.map(b => b.name).join('/'),
        },
      };

      // Check if this EXACT file is already in selected list
      // Use a composite key of name + parentFolder + path to ensure uniqueness
      if (
        !this.selectedFiles.some(
          item =>
            item.name === file.name &&
            item.parentFolder === parentFolderName &&
            (item.parentPath === filePath || (!item.parentPath && !filePath)) &&
            item.type === file.type,
        )
      ) {
        this.selectedFiles.push(selectedFile);
        console.log(
          `Selected file: ${selectedFile.name} from ${selectedFile.fullPath} (${selectedFile.size}, ${selectedFile.date})`,
          `Location: ${JSON.stringify(selectedFile.locationInfo)}`,
        );
      }
    } else {
      // When deselecting a file, remove it from selectedFiles
      // Make sure to match by BOTH name AND parentFolder AND parentPath to only remove the specific instance
      this.selectedFiles = this.selectedFiles.filter(
        item =>
          !(
            item.name === file.name &&
            item.parentFolder === parentFolderName &&
            (item.parentPath === filePath || (!item.parentPath && !filePath)) &&
            item.type === file.type
          ),
      );
    }

    // Always update parent folder selection state if there is a parent folder
    if (parentFolder) {
      this.updateFolderSelectionState(parentFolder);

      // Check if parent folder is in the selectedFiles list and update its counts
      const parentFolderInSelectedList = this.selectedFiles.find(
        item => item.type === 'folder' && item.name === parentFolderName,
      );

      if (parentFolderInSelectedList) {
        // Force update the folder in the selected files to refresh its subtitle counts
        this.forceUpdateFolderInSelectedFiles(parentFolderName);
        this.forceUpdateParentFolderInSelectedFiles(parentFolderName);
      }
    }

    // Update the file in the flattened items array to ensure tree view consistency
    this.updateFileInFlattenedItems(file, parentFolderName);

    // Update duplicates state
    this.updateDuplicatesState();
  }

  /**
   * Removes a file from the selected files list
   * Modified to handle files with the same name from different folders
   */
  removeSelectedFile(file: FileItem): void {
    // Remove specific file instance from selectedFiles array
    this.selectedFiles = this.selectedFiles.filter(
      item => !(item.name === file.name && item.parentFolder === file.parentFolder),
    );

    // Update the selection state in the original file list
    this.updateSourceFileSelectionState(file, false);

    // Also explicitly update in the flattened items for display consistency
    const flatItem = this.flattenedItems.find(
      item => item.name === file.name && item.parentPath === file.parentFolder && item.type === file.type,
    );

    if (flatItem) {
      flatItem.selected = false;
    }

    // Update duplicates state
    this.updateDuplicatesState();
  }

  /**
   * Updates a file's selection state in the original file list
   * Modified to handle files with the same name from different folders
   */
  updateFileSelectionInList(fileList: FileItem[], fileToUpdate: FileItem): void {
    const parentFolderName = fileToUpdate.parentFolder;
    // If we know the parent folder, use it to find the exact file
    if (parentFolderName) {
      // Find the parent folder first
      for (const item of fileList) {
        if (item.type === 'folder' && item.name === parentFolderName && item.children) {
          // Find and update the file in this folder
          const fileInFolder = item.children.find(child => child.name === fileToUpdate.name);
          if (fileInFolder) {
            fileInFolder.selected = false;
            return;
          }
        }
      }
    }
    // Fallback to the old method if parent folder approach fails
    fileList.forEach(item => {
      if (
        (item.type === 'file' || item.type === 'image') &&
        item.name === fileToUpdate.name &&
        item.size === fileToUpdate.size &&
        item.date === fileToUpdate.date
      ) {
        item.selected = false;
      } else if (item.type === 'folder' && item.children) {
        this.updateFileSelectionInList(item.children, fileToUpdate);
      }
    });
  }

  /**
   * Checks if a file is already in the selected files list
   * Modified to check by reference instead of just by name
   */
  isFileInSelectedList(file: FileItem): boolean {
    // For files in folders, we need to track which folder they came from
    if (file.type !== 'folder') {
      // Get parent folder information
      const parentFolder = this.findParentFolder(this.clioFiles, file);
      const parentFolderName = parentFolder
        ? parentFolder.name
        : file.parentFolder ||
          (this.breadcrumbs.length > 1 ? this.breadcrumbs[this.breadcrumbs.length - 1].name : 'Root');

      // Check for file with same name AND parent folder
      return this.selectedFiles.some(
        item => item.name === file.name && item.parentFolder === parentFolderName && item.type === file.type,
      );
    }

    // For folders, check by name and path
    return this.selectedFiles.some(
      item =>
        item.name === file.name &&
        item.type === 'folder' &&
        (item.parentFolder === file.parentFolder ||
          (item.parentPath === file.parentPath && !item.parentFolder && !file.parentFolder)),
    );
  }

  /**
   * Find the parent folder of a file
   */
  findParentFolder(fileList: FileItem[], targetFile: FileItem): FileItem | null {
    for (const item of fileList) {
      if (item.type === 'folder' && item.children) {
        for (const child of item.children) {
          if (child === targetFile) {
            return item;
          }
        }
        // Recursive search in nested folders
        const foundInNestedFolder = this.findParentFolder(
          item.children.filter(child => child.type === 'folder') as FileItem[],
          targetFile,
        );
        if (foundInNestedFolder) {
          return foundInNestedFolder;
        }
      }
    }
    return null;
  }

  /**
   * Gets the current date formatted as MM/DD/YYYY
   */
  getCurrentDate(): string {
    const date = new Date();
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  /**
   * Check if a file is a duplicate in NuageDx files
   * This checks only by name for the warning icon
   */
  checkIfDuplicate(fileName: string): boolean {
    // Check if the file exists in any of the NuageDx folders
    for (const folder of this.nuageDxFiles) {
      if (folder.children && folder.children.some(file => file.name === fileName)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check for exact duplicates (name, size, and date match)
   * This now correctly checks for duplicates across different folders
   */
  checkForExactDuplicates(): boolean {
    // Use the debug version for all checks to ensure consistency
    return this.checkForExactDuplicatesDebug();
  }

  /**
   * Check for exact duplicates with extended debugging
   */
  checkForExactDuplicatesDebug(): boolean {
    // Reset duplicate flags on all selected files
    this.selectedFiles.forEach(file => {
      file.isDuplicate = false;
    });
    // Update duplicatedFiles array with exact duplicates
    this.duplicatedFiles = [];
    console.log(`Checking ${this.selectedFiles.length} files for duplicates...`);
    // Early return if we don't have enough files to have duplicates
    if (this.selectedFiles.length < 2) {
      console.log('Not enough files to check for duplicates');
      return false;
    }
    // Group by name first for easier debugging
    const filesByName = new Map<string, FileItem[]>();
    for (const file of this.selectedFiles) {
      if (!filesByName.has(file.name)) {
        filesByName.set(file.name, []);
      }
      filesByName.get(file.name)?.push(file);
    }
    // Log files grouped by name
    console.log('Files grouped by name:');
    filesByName.forEach((files, name) => {
      console.log(`${name}: ${files.length} files`);
    });
    // Check each name group for exact duplicates
    filesByName.forEach((files, name) => {
      if (files.length > 1) {
        // Group by size and date
        const exactMatches = new Map<string, FileItem[]>();
        for (const file of files) {
          const key = `${file.size}_${file.date}`;
          if (!exactMatches.has(key)) {
            exactMatches.set(key, []);
          }
          exactMatches.get(key)?.push(file);
        }
        // Check each group for exact duplicates (same name, size, date)
        exactMatches.forEach((exactFiles, key) => {
          console.log(`Group ${name} with ${key}: ${exactFiles.length} files`);
          if (exactFiles.length > 1) {
            // We have duplicates!
            console.log(`FOUND DUPLICATES: ${exactFiles.length} files with name=${name}, size/date=${key}`);
            // Mark all files in group as duplicates
            exactFiles.forEach(file => {
              file.isDuplicate = true;
              // Add each duplicate file individually to the duplicatedFiles array
              this.duplicatedFiles.push({
                name: file.name,
                type: file.type,
                selected: true,
                size: file.size,
                date: file.date,
                parentFolder: file.parentFolder,
              });
            });
          }
        });
      }
    });
    return this.duplicatedFiles.length > 0;
  }

  /**
   * Update the duplicates state whenever selection changes
   */
  updateDuplicatesState(): void {
    this.hasExactDuplicates = this.checkForExactDuplicates();
  }

  // Process files in batches for better UX
  async processBatch(files: FileItem[], transferButton, transferType) {
    try {
      console.log('Processing batch of files:', files);

      // Call the actual API to share the files - PRESERVE ORIGINAL FUNCTIONALITY
      await this.matters_$.importFilesFromClio(files, this.caseName, transferType, this.data.clioDocId);

      // Add successful log entries for each file
      files.forEach(file => {
        this.addTransferLogItem({
          name: file.name,
          status: 'success',
          timestamp: new Date(),
          details: 'File shared successfully',
          parentFolder: file.parentFolder || 'Root',
        });
      });

      // Mark files as transferred in the source list
      this.markTransferredFilesInSource(files);
      console.log('Files transferred successfully');

      // Update nuageDxFiles by re-fetching data from backend after successful transfer
      try {
        // Process NuageDX matter content to update the file list
        await this.processNuageDxMatterContent(this.caseName);
        console.log('Successfully refreshed nuageDxFiles after transfer');

        // Update UI to reflect changes
        this.updateFlattenedItems();
        this.updateItemVisibility();

        // Check if any folders should be marked as fully synced
        this.checkFoldersForFullSync();
      } catch (error) {
        console.error('Error refreshing NuageDX file list after transfer:', error);
      }
    } catch (error) {
      console.error('Error during file transfer:', error);

      // Add error log entries for each file in the batch
      files.forEach(file => {
        this.addTransferLogItem({
          name: file.name,
          status: 'error',
          timestamp: new Date(),
          details: `Transfer failed - ${error?.message || 'API error'}`,
          parentFolder: file.parentFolder || 'Root',
        });
      });
    } finally {
      // Update UI state
      this.selectedFiles = this.selectedFiles.filter(file => !files.includes(file));
      this.updateDuplicatesState();

      // Re-enable button when all processing is done
      if (transferButton) {
        transferButton.disabled = false;
      }

      // Turn off progress indicator
      this.transferInProgress = false;
    }
  }

  /**
   * Method to handle file transfer and show transfer log
   */
  transferSelectedFiles(): void {
    // Show loading indicator or disable button during processing
    const transferButton = document.querySelector('.transfer-button') as HTMLButtonElement;
    if (transferButton) {
      transferButton.disabled = true;
    }

    // Set transfer in progress state to show spinner
    this.transferInProgress = true;

    // Get selected files for transfer
    const filesToTransfer = this.selectedFiles.filter(file => file.selected);

    if (filesToTransfer.length === 0) {
      console.error('No files selected for transfer');
      if (transferButton) {
        transferButton.disabled = false;
      }
      this.transferInProgress = false;
      return;
    }

    // Show the transfer log panel if it's not already visible
    this.showTransferLog = true;

    // Force update duplicates state before transfer - PRESERVE ORIGINAL FUNCTIONALITY
    this.updateDuplicatesState();
    console.log('Transferring files...', {
      selectedFiles: this.selectedFiles,
      hasDuplicates: this.hasExactDuplicates,
      duplicatedFiles: this.duplicatedFiles,
    });

    // Start the transfer process
    this.processBatch(filesToTransfer, transferButton, this.transferType);
  }

  /**
   * Check all folders to see if they should be marked as fully synced
   * Called after file transfer and nuageDxFiles refresh
   */
  checkFoldersForFullSync(): void {
    // Recursive function to check folders and their children
    const checkFolder = (folder: FileItem): boolean => {
      if (!folder.children || folder.children.length === 0) {
        return true; // Empty folders are considered synced
      }

      // Check if all children are transferred or synced
      const allSynced = folder.children.every(child => {
        if (child.type === 'file' || child.type === 'image') {
          return child.alreadyTransferred === true;
        } else if (child.type === 'folder') {
          return checkFolder(child);
        }
        return false;
      });

      // Update folder's synced state
      folder.accessibilityLabel = allSynced ? 'Already synced' : '';

      return allSynced;
    };

    // Process all root folders
    this.clioFiles.forEach(item => {
      if (item.type === 'folder') {
        checkFolder(item);
      }
    });

    // Update flattened items to reflect changes in the UI
    this.updateFlattenedItems();
    this.updateItemVisibility();
  }

  /**
   * Mark files as transferred in the source list after successful transfer
   */
  markTransferredFilesInSource(transferredFiles: FileItem[]): void {
    transferredFiles.forEach(file => {
      // Find the file in the original source list
      const sourceFile = this.findFileInSource(file);
      if (sourceFile) {
        sourceFile.alreadyTransferred = true;
        sourceFile.transferredClass = 'already-transferred';
        sourceFile.accessibilityLabel = 'Already transferred';
        sourceFile.hasCheckmark = true;

        // If it's a folder, mark all its children as transferred too
        if (sourceFile.type === 'folder' && sourceFile.children) {
          this.markChildrenAsTransferred(sourceFile.children);
        }
      }
    });

    // Update flattened items to reflect the changes
    this.updateFlattenedItems();

    // Filter out transferred files from the right panel
    this.filterTransferredFilesFromSelected();
  }

  /**
   * Recursive helper method to mark all children of a folder as transferred
   */
  markChildrenAsTransferred(items: FileItem[]): void {
    if (!items || !Array.isArray(items)) return;

    items.forEach(item => {
      // Mark as transferred
      item.alreadyTransferred = true;
      item.transferredClass = 'already-transferred';
      item.accessibilityLabel = 'Already transferred';
      item.hasCheckmark = true;

      // Recursively process children of folders
      if (item.type === 'folder' && item.children) {
        this.markChildrenAsTransferred(item.children);
      }
    });
  }

  /**
   * Find a file in the source list
   */
  findFileInSource(file: FileItem): FileItem | null {
    // Helper function to recursively search for a file
    const findInList = (list: FileItem[], file: FileItem): FileItem | null => {
      for (const item of list) {
        // Check if this is the file we're looking for
        if (
          item.type === file.type &&
          item.name === file.name &&
          (item.parentPath === file.parentPath || (!item.parentPath && !file.parentPath))
        ) {
          return item;
        }

        // If this is a folder, search its children
        if (item.type === 'folder' && item.children) {
          const found = findInList(item.children, file);
          if (found) return found;
        }
      }

      return null;
    };

    return findInList(this.clioFiles, file);
  }

  /**
   * Add a new item to the transfer log
   */
  addTransferLogItem(logItem: TransferLogItem): void {
    // Add the item to the beginning of the array so newest items appear first
    this.transferLogItems.unshift({
      ...logItem,
      isNew: true, // Add a flag to style newly added items with animation
    });

    // Remove the isNew flag after animation completes
    setTimeout(() => {
      if (this.transferLogItems[0]) {
        this.transferLogItems[0].isNew = false;
      }
    }, 500);

    // Limit the number of log items to prevent UI clutter (optional)
    if (this.transferLogItems.length > 50) {
      this.transferLogItems = this.transferLogItems.slice(0, 50);
    }
  }

  /**
   * Clear the transfer log
   */
  clearTransferLog(): void {
    this.transferLogItems = [];
  }

  cancelTransfer(): void {
    // Logic for canceling transfer
    console.log('Transfer canceled');
    // Reset transfer in progress if it was active
    this.transferInProgress = false;
  }

  changeResolutionOption(option: string): void {
    this.resolutionOptions.forEach(opt => {
      opt.checked = opt.value === option;
    });
    this.selectedResolutionOption = option;
  }

  /**
   * Gets a preview of a renamed file for display purposes
   * Uses a simple pattern with underscore and number
   */
  getRenamingPreview(fileName: string): string {
    if (!fileName) return '';
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return `${fileName}_1`;
    } else {
      const name = fileName.substring(0, lastDotIndex);
      const extension = fileName.substring(lastDotIndex);
      return `${name}_1${extension}`;
    }
  }

  navigateBackToClio(): void {
    console.log('Navigating back to Clio');
    const clioFolderId = this.data.clioFolderId;
    if (clioFolderId) {
      window.open(`https://app.clio.com/nc/#/matters/${clioFolderId}/documents`, '_blank');
    }
  }

  closeDialog(): void {
    console.log('Closing dialog');
    this.dialogRef.close();
  }

  /**
   * Called when a file's checkbox is changed in the selected files panel
   */
  onSelectedFileCheckboxChange(file: FileItem): void {
    console.log(`Checkbox changed for file ${file.name}, new state: ${file.selected}`);

    // If the file is being unchecked, handle the uncheck logic
    if (!file.selected) {
      this.handleFileUncheck(file);
    } else {
      // Handle the logic for when a file is checked (if needed)
      // For example, you might want to add it to a selected list
    }
  }

  /**
   * Updates the source file selection state after a selection in the selected files panel
   * Enhanced with better folder reference handling to ensure counts update properly
   */
  updateSourceFileSelectionState(file: FileItem, selected: boolean): void {
    let fileUpdated = false;

    // First try to find the file in the flattened items
    const flatItem = this.flattenedItems.find(
      item =>
        item.name === file.name &&
        item.parentPath === file.parentFolder &&
        item.type === file.type &&
        item.size === file.size &&
        item.date === file.date,
    );

    if (flatItem) {
      flatItem.selected = selected;
      fileUpdated = true;

      // If the file has a parent folder, update its selection state
      if (file.parentFolder) {
        const parentFolderInFlattened = this.flattenedItems.find(
          item => item.type === 'folder' && item.name === file.parentFolder,
        );
        if (parentFolderInFlattened) {
          parentFolderInFlattened._needsCountRefresh = true;
          parentFolderInFlattened._lastCountRefreshTime = Date.now();
        }

        // Update any parent folder in the selectedFiles list to refresh its UI
        this.forceUpdateFolderInSelectedFiles(file.parentFolder);
      }
    }

    // Look in the parent folder for the file
    const parentFolderName = file.parentFolder;
    if (parentFolderName) {
      const folder = this.findFolderInSource(parentFolderName, '');
      if (folder && folder.children) {
        const fileInFolder = folder.children.find(child => child.name === file.name);
        if (fileInFolder) {
          fileInFolder.selected = selected;
          fileUpdated = true;

          // Mark the folder as needing count refresh
          folder._needsCountRefresh = true;
          folder._lastCountRefreshTime = Date.now();

          // Update folder selection state based on children
          this.updateFolderSelectionState(folder);

          // Force update the folder in the selected files list
          this.forceUpdateFolderInSelectedFiles(parentFolderName);
        }
      }
    }

    // If the file wasn't found by parent folder, try to find it by name, size, and date
    if (!fileUpdated) {
      this.findAndUpdateFileInList(this.clioFiles, file, selected);
    }

    // Always update the UI display
    this.updateFlattenedItems();
    this.updateItemVisibility();
  }

  /**
   * Helper method to find and update a file's selection state in a file list
   */
  findAndUpdateFileInList(fileList: FileItem[], fileToUpdate: FileItem, selected: boolean): boolean {
    for (const item of fileList) {
      if (
        (item.type === 'file' || item.type === 'image') &&
        item.name === fileToUpdate.name &&
        (item.size === fileToUpdate.size || !item.size) &&
        (item.date === fileToUpdate.date || !item.date)
      ) {
        // Additionally, check the parent path for exact file matching
        const itemParent = this.findParentFolder(this.clioFiles, item);
        const updateParent = fileToUpdate.parentFolder || null;

        // Only update if the paths match or both don't have parents
        if (itemParent?.name === updateParent || (!itemParent && !updateParent)) {
          item.selected = selected;
          return true;
        }
      } else if (item.type === 'folder' && item.children) {
        if (this.findAndUpdateFileInList(item.children, fileToUpdate, selected)) {
          // Update folder selection state based on children
          this.updateFolderSelectionState(item);
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Update folder selection state based on its children's selection states
   */
  updateFolderSelectionState(folder: FileItem): void {
    if (!folder.children || folder.children.length === 0) return;

    // Set folder selected state to true if all children are selected,
    // false if none are selected
    const allSelected = folder.children.every(child => child.selected);
    const noneSelected = folder.children.every(child => !child.selected);

    if (allSelected) {
      // When all children are selected, the folder must be selected
      folder.selected = true;
      folder.partiallySelected = false; // Make sure to reset partiallySelected
      console.log(`Folder ${folder.name} is fully selected`);
    } else if (noneSelected) {
      // When no children are selected, the folder must be unselected
      folder.selected = false;
      folder.partiallySelected = false; // Make sure to reset partiallySelected
      console.log(`Folder ${folder.name} is fully unselected`);

      // Also ensure the folder is removed from selectedFiles when all children are unselected
      this.selectedFiles = this.selectedFiles.filter(
        item =>
          !(
            item.type === 'folder' &&
            item.name === folder.name &&
            (item.parentFolder === folder.parentPath || (!item.parentFolder && !folder.parentPath))
          ),
      );
    } else {
      // For partial selection, set selected to false but use partiallySelected flag
      folder.selected = false;
      folder.partiallySelected = true; // Explicitly set the partial selection state
      console.log(`Folder ${folder.name} is partially selected`);

      // Ensure folder itself is not in selectedFiles when partially selected
      this.selectedFiles = this.selectedFiles.filter(
        item =>
          !(
            item.type === 'folder' &&
            item.name === folder.name &&
            (item.parentFolder === folder.parentPath || (!item.parentFolder && !folder.parentPath))
          ),
      );
    }

    // If folder has a parent folder, recursively update the parent's selection state as well
    if (folder.parentPath) {
      const pathParts = folder.parentPath.split('/');
      const parentFolderName = pathParts[pathParts.length - 1];
      const parentPath = pathParts.slice(0, -1).join('/');
      const parentFolder = this.findFolderInSource(parentFolderName, parentPath);

      if (parentFolder) {
        this.updateFolderSelectionState(parentFolder);
      }
    }
  }

  /**
   * Sync folder with children selection
   */
  syncFolderWithChildrenSelection(): void {
    // Update all folder selection states based on their children
    this.clioFiles.forEach(item => {
      if (item.type === 'folder' && item.children) {
        this.updateFolderSelectionState(item);
      }
    });
  }

  /**
   * Update all folder selection states to match their children's selection
   * Called at important points to ensure UI consistency
   */
  updateAllFolderSelectionStates(): void {
    // Process all folders to sync their selected state with their children
    this.clioFiles.forEach(item => {
      if (item.type === 'folder' && item.children) {
        this.updateFolderSelectionState(item);
      }
    });
  }

  /**
   * Navigate to matter details when matter ID is clicked
   */
  navigateToMatter(): void {
    if (!this.matterId) {
      // If no matter ID, show an error message
      this.auth_$.setToastMessage('No matter ID found', 'error');
      return;
    }

    if (!this.auth_$.uid) {
      // If no user ID, show an error message
      this.auth_$.setToastMessage('No user ID found', 'error');
      return;
    }

    this.closeDialog();
    this.matters_$.openClioClientMatter(this.matterId, this.auth_$.uid, {});
  }

  /**
   * Toggle search input visibility
   */
  toggleSearch(): void {
    this.showSearchInput = !this.showSearchInput;

    // If search is being shown, focus the input after a brief delay to allow animation
    if (this.showSearchInput) {
      setTimeout(() => {
        const searchInputElement = document.querySelector('.search-container.active input') as HTMLInputElement;
        if (searchInputElement) {
          searchInputElement.focus();
        }
      }, 300); // 300ms delay to match the CSS transition
    } else {
      // Clear search when hiding
      this.clearSearch();
    }
  }

  /**
   * Clear search and reset filtered results
   * @param searchInput Optional input element reference to clear
   */
  clearSearch(searchInput?: HTMLInputElement): void {
    this.searchQuery = '';

    // Clear the input field if provided
    if (searchInput) {
      searchInput.value = '';
    }

    // Reset filtered content to show current folder
    this.filteredFolderContents = [...this.currentFolderContents];

    // Check if all files in the current folder are already transferred
    this.checkIfAllFilesTransferred();
  }

  /**
   * Search for files in Clio using a cloud function API call
   * This is triggered by the search button click
   * @param searchValue The search value from the input field
   */
  searchClioFiles(searchValue?: string): void {
    // Store the search query for display purposes
    this.searchQuery = searchValue ? searchValue.trim() : '';

    if (!this.searchQuery) {
      return;
    }

    // Set loading state
    this.isSearching = true;
    this.fileCheckingMessage = 'Searching Clio for files...';
    this.fileCheckingComplete = false;

    // Show a message while searching
    console.log(`Searching Clio for files matching: "${this.searchQuery}"`);

    // Call the Clio search API through a cloud function
    const url = `${environment.constants.cloudfunctionsURL}clio-searchClioFiles`;

    this.http
      .post<any>(url, {
        searchQuery: this.searchQuery,
        matterId: this.matterId,
        eu: false,
        userdocid: this.matters_$.auth_$.userData.getValue()['id'],
      })
      .pipe(
        catchError(error => {
          console.error('Error searching Clio files:', error);
          // Show error message
          this.isSearching = false;
          this.fileCheckingComplete = true;
          this.fileCheckingMessage = 'Search failed. Please try again.';

          // Return empty array on error
          return [];
        }),
      )
      .subscribe(response => {
        // Process the search results
        console.log('Search results:', response);

        if (response && response.data && Array.isArray(response.data)) {
          // Map the API response to our FileItem format
          this.filteredFolderContents = this.mapSearchResultsToFileItems(response.data);

          // Mark transferred files in the search results
          this.markTransferredInContents(this.filteredFolderContents);

          // Clear the search if no results
          if (this.filteredFolderContents.length === 0) {
            console.log('No search results found');
          }
        } else if (response && Array.isArray(response)) {
          // Direct array response format
          this.filteredFolderContents = this.mapSearchResultsToFileItems(response);

          // Mark transferred files in the search results
          this.markTransferredInContents(this.filteredFolderContents);

          if (this.filteredFolderContents.length === 0) {
            console.log('No search results found');
          }
        } else {
          // Handle empty or invalid response
          this.filteredFolderContents = [];
          console.log('Invalid search response or no results');
        }

        // Reset loading state
        this.isSearching = false;
        this.fileCheckingComplete = true;
        this.fileCheckingMessage = 'Search complete';

        // Check if all files in the search results are already transferred
        this.checkIfAllFilesTransferred();
      });
  }

  /**
   * Navigate to the parent folder of a file when clicking its location
   * Enhanced to ensure the file is also selected after navigation
   */
  async navigateToParentFolder(file: FileItem): Promise<void> {
    console.log('Navigating to parent folder for file:', file);

    // Store the file reference for later selection
    const fileToSelect = file;

    // Get the fullPath value if available, otherwise construct from parentFolder
    const fullPath = file.fullPath || (file.parentFolder ? `Root/${file.parentFolder}` : 'Root');
    console.log('Full path:', fullPath);

    // If path is just "Root", navigate to root
    if (fullPath === 'Root') {
      console.log('Navigating to root level');
      await this.navigateToBreadcrumb(0);

      // After navigation, highlight the file in the left panel
      setTimeout(() => this.highlightFileInLeftPanel(fileToSelect), 100);
      return;
    }

    // Parse the path into segments
    const pathSegments = this.parsePathForDisplay(fullPath);
    console.log('Path segments:', pathSegments);

    // Skip the "Root" segment and get all folder names in the path
    const folderNames = pathSegments.filter(segment => segment.name !== 'Root').map(segment => segment.name);

    if (folderNames.length === 0) {
      // No folder segments, just navigate to root
      await this.navigateToBreadcrumb(0);

      // After navigation, highlight the file in the left panel
      setTimeout(() => this.highlightFileInLeftPanel(fileToSelect), 100);
      return;
    }

    // Navigate to the deepest folder in the path
    await this.navigateToNestedFolder(folderNames)
      .then(() => {
        // After successful navigation, highlight the file in the left panel
        setTimeout(() => this.highlightFileInLeftPanel(fileToSelect), 100);
      })
      .catch(error => {
        console.error('Error navigating to parent folder:', error);
      });
  }

  /**
   * Flattens the entire file structure into a single array for tree display
   * Also sets the depth and visibility of each item
   */
  updateFlattenedItems(): void {
    // Store the selection state of existing items before replacing the array
    const selectionMap = new Map<string, boolean>();

    this.flattenedItems.forEach(item => {
      // Create a unique key for each item that includes its path
      const key = `${item.name}|${item.parentPath || ''}|${item.type}`;
      selectionMap.set(key, !!item.selected);
    });

    // Reset the flattened items array
    this.flattenedItems = [];

    // Build a properly ordered tree structure
    this.flattenItems(this.clioFiles, 0, '');

    // Restore selection states from the map to preserve selections through rebuilds
    this.flattenedItems.forEach(item => {
      const key = `${item.name}|${item.parentPath || ''}|${item.type}`;

      // If this item was previously selected, keep it selected
      // Otherwise maintain its current selection state (from clioFiles)
      if (selectionMap.has(key)) {
        item.selected = selectionMap.get(key) || item.selected;
      }
    });

    // Update visibility based on expanded folders
    this.updateItemVisibility();

    console.log('Updated flattened items:', this.flattenedItems.length);
  }

  /**
   * Recursively flatten the file structure while preserving parent-child relationships
   * This method now also handles the sorting (folders first at each level)
   */
  flattenItems(items: FileItem[], depth: number, parentPath: string): void {
    if (!items || !Array.isArray(items)) {
      console.warn('Invalid items array in flattenItems:', items);
      return;
    }

    // First sort the items at this level - folders first
    const sortedItems = [...items].sort((a, b) => {
      // Folders come before files
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;

      // If both are the same type, sort alphabetically
      return a.name.localeCompare(b.name);
    });

    // Now process the sorted items
    sortedItems.forEach((item, index) => {
      if (!item) {
        console.warn(`Null or undefined item at index ${index}`);
        return;
      }

      // Create a clone of the item with depth information
      const flatItem = { ...item };
      flatItem.depth = depth;

      // Set parent path for location tracking
      flatItem.parentPath = parentPath;

      // Add console log to check depth and name of root items
      if (depth === 0) {
        console.log('Flattening root item:', {
          name: flatItem.name,
          type: flatItem.type,
          depth: flatItem.depth,
          sourceObjectName: this.sourceObjectName,
        });
      }

      // Add to flattened array
      this.flattenedItems.push(flatItem);

      // If this is a folder with children, immediately process them
      // This ensures children appear right after their parent in the flat list
      if (item.type === 'folder' && item.children && Array.isArray(item.children)) {
        // Build the full path for this folder's children
        const itemPath = parentPath ? `${parentPath}/${item.name}` : item.name;

        // Process children with an increased depth level
        // Add console log here to inspect nested item depth
        item.children.forEach(child => {
          console.log('Flattening nested item:', {
            name: child.name,
            type: child.type,
            depth: depth + 1,
            parentFolder: item.name,
          });
        });

        this.flattenItems(item.children, depth + 1, itemPath);
      }
    });
  }

  /**
   * Updates visibility of items based on expanded folders
   */
  updateItemVisibility(): void {
    // First reset all visibility
    this.flattenedItems.forEach(item => {
      item.visible = false;
    });

    // Build a set of visible paths based on expanded folders
    const visiblePaths = new Set<string>();
    visiblePaths.add(''); // Root path is always visible

    // For MATTER or FOLDER type, ensure the root folder's children are visible
    if ((this.data.type === 1 || this.data.type === 2) && this.sourceObjectName && this.clioFiles.length === 1) {
      const rootFolder = this.clioFiles[0];
      if (rootFolder.type === 'folder' && rootFolder.name === this.sourceObjectName && rootFolder.expanded) {
        // Add the root folder's path to visible paths
        visiblePaths.add(rootFolder.name);
      }
    }

    // Add all expanded folder paths
    for (let item of this.flattenedItems) {
      if (item.type === 'folder' && item.expanded && item.parentPath !== undefined) {
        visiblePaths.add(item.parentPath ? `${item.parentPath}/${item.name}` : item.name);
      }
    }

    // Mark items as visible if their parent path is in the visible paths set
    // OR if they are at depth 0 (top level)
    for (let item of this.flattenedItems) {
      if (visiblePaths.has(item.parentPath || '') || item.depth === 0) {
        // Added condition for depth 0
        item.visible = true;
      }
    }

    // Debug log to help diagnose visibility issues
    console.log('Visible paths:', Array.from(visiblePaths));
    console.log('Visible items count:', this.flattenedItems.filter(item => item.visible).length);
  }

  /**
   * Gets items to display in the tree view based on current state
   * Filters out already transferred files
   */
  getDisplayedItems(): FileItem[] {
    // If search is active, show all matching items with special sorting
    let items: FileItem[] = [];

    console.log('getDisplayedItems: Checking root items for icon condition values.');
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      items = this.filteredFolderContents;
    } else {
      // For normal view, get the visible items which are already properly ordered
      items = this.flattenedItems.filter(item => item.visible);
    }

    // Log root item properties to debug icon visibility condition
    items.forEach(item => {
      if (item.depth === 0) {
        console.log('getDisplayedItems - Root Item Check:', {
          name: item.name,
          depth: item.depth,
          sourceObjectName: this.sourceObjectName,
        });
      }
    });

    // Filter out already transferred items
    return items;
  }

  /**
   * Sorts an array of FileItems to display folders first, then files
   * While preserving the parent-child relationship in the tree structure
   */
  sortItemsFoldersFirst(items: FileItem[]): FileItem[] {
    // For a flattened view like our tree, we need to make sure children stay with their parents
    // First, create a hierarchical structure from the flattened items
    const itemsByPath: { [key: string]: FileItem[] } = {};

    // Group items by their parent path
    items.forEach(item => {
      const parentPath = item.parentPath || '';
      if (!itemsByPath[parentPath]) {
        itemsByPath[parentPath] = [];
      }
      itemsByPath[parentPath].push(item);
    });

    // For each group, sort folders first within that group
    Object.keys(itemsByPath).forEach(path => {
      itemsByPath[path].sort((a, b) => {
        // Folders come first
        if (a.type === 'folder' && b.type !== 'folder') return -1;
        if (a.type !== 'folder' && b.type === 'folder') return 1;

        // If both are the same type, sort alphabetically
        return a.name.localeCompare(b.name);
      });
    });

    // This will hold our final sorted list
    const result: FileItem[] = [];

    // Define a recursive function to add items in the right order
    const addItemsInOrder = (path: string, depth: number) => {
      // Get all items at this path level
      const itemsAtPath = itemsByPath[path] || [];

      // Process all items at this level
      for (const item of itemsAtPath) {
        // Add the current item
        result.push(item);

        // If this is a folder that's expanded, immediately add its children
        if (item.type === 'folder' && item.expanded) {
          // Calculate the full path for this folder's children
          const childPath = path ? `${path}/${item.name}` : item.name;
          // Recursively add all children
          addItemsInOrder(childPath, depth + 1);
        }
      }
    };

    // Start with the root items (empty path)
    addItemsInOrder('', 0);

    return result;
  }

  /**
   * Get depth level for an item (for indentation)
   * Ensures a valid numeric value is always returned
   */
  getItemDepth(item: FileItem): number {
    return typeof item.depth === 'number' ? item.depth : 0;
  }

  /**
   * Update breadcrumbs when a folder is expanded in the tree
   */
  updateBreadcrumbsForFolder(folder: FileItem): void {
    // Start with root breadcrumb
    const newBreadcrumbs: BreadcrumbItem[] = [{ name: 'Home', folder: null }];

    // If no parent path, this is a root folder
    if (!folder.parentPath) {
      newBreadcrumbs.push({ name: folder.name, folder: folder });
    } else {
      // Build path by traversing parent paths
      const pathParts = folder.parentPath.split('/');
      let currentPath = '';

      // Add each ancestor folder to breadcrumbs
      for (const part of pathParts) {
        if (part) {
          // Find the folder item for this part
          const pathFolder = this.findFolderByPath(currentPath, part);
          if (pathFolder) {
            newBreadcrumbs.push({ name: pathFolder.name, folder: pathFolder });
          }

          // Update current path
          currentPath = currentPath ? `${currentPath}/${part}` : part;
        }
      }

      // Add the current folder as the last breadcrumb
      newBreadcrumbs.push({ name: folder.name, folder: folder });
    }

    // Update breadcrumbs
    this.breadcrumbs = newBreadcrumbs;

    // Update current folder contents to match what's in the expanded folder
    this.currentFolderContents = folder.children || [];

    console.log(`Updated breadcrumbs for folder: ${folder.name}`, this.breadcrumbs);
  }

  /**
   * Find a folder by path and name
   */
  findFolderByPath(parentPath: string, folderName: string): FileItem | null {
    return (
      this.flattenedItems.find(
        item => item.type === 'folder' && item.name === folderName && (item.parentPath || '') === parentPath,
      ) || null
    );
  }

  /**
   * Expand all parent folders of a given folder
   */
  expandParentFolders(folder: FileItem): void {
    if (!folder.parentPath) return;

    const pathParts = folder.parentPath.split('/');
    let currentPath = '';

    for (const part of pathParts) {
      if (part) {
        const parentFolder = this.findFolderByPath(currentPath, part);
        if (parentFolder) {
          parentFolder.expanded = true;
        }

        currentPath = currentPath ? `${currentPath}/${part}` : part;
      }
    }
  }

  /**
   * Closes ALL other expanded folders in the tree
   * This ensures only one folder is open at a time
   */
  closeAllFolders(exceptFolder: FileItem): void {
    // First close all folders in the flattened items array
    this.flattenedItems.forEach(item => {
      // Skip the folder we want to keep open
      if (item === exceptFolder) return;

      // Skip the root folder if it's the sourceObjectName folder
      if (item.name === this.sourceObjectName) return;

      // Close all other folders
      if (item.type === 'folder' && item.expanded) {
        item.expanded = false;
        console.log(`Closed folder: ${item.name} to ensure only one folder is open`);
      }
    });

    // Also close folders in the original data structure
    const closeInSource = (items: FileItem[]): void => {
      if (!items || !Array.isArray(items)) return;

      items.forEach(item => {
        // Skip the folder we want to keep open by comparing name and path
        if (item === exceptFolder || (item.name === exceptFolder.name && item.parentPath === exceptFolder.parentPath)) {
          return;
        }

        // Skip the root folder if it's the sourceObjectName folder
        if (item.name === this.sourceObjectName) {
          return;
        }

        // Close all other folders
        if (item.type === 'folder') {
          item.expanded = false;

          // Recursively close nested folders
          if (item.children) {
            closeInSource(item.children);
          }
        }
      });
    };

    // Start checking from the root items
    closeInSource(this.clioFiles);
  }

  /**
   * Filter files based on search query - updated for tree view
   * Also filters out already transferred files
   */
  filterFiles(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      // If no search query, show current folder items only (excluding transferred files)
      this.filteredFolderContents = [...this.currentFolderContents].filter(
        item => !this.isFileAlreadyTransferred(item),
      );
    } else {
      const query = this.searchQuery.toLowerCase().trim();

      // When searching, we search across all items, not just the current folder
      // This makes search more useful with the tree structure
      const matchingItems = this.flattenedItems.filter(
        item => item.name.toLowerCase().includes(query) && !this.isFileAlreadyTransferred(item),
      );

      // Sort with folders first even in search results
      this.filteredFolderContents = this.sortItemsFoldersFirst(matchingItems);
    }
  }

  /**
   * Find a folder by name and path
   */
  findFolderByNameAndPath(fileList: FileItem[], folderName: string, parentPath: string): FileItem | null {
    // Check direct children first
    for (const item of fileList) {
      if (item.type === 'folder' && item.name === folderName && (item.parentPath || '') === parentPath) {
        return item;
      }
    }

    // Check recursively in subfolders
    for (const item of fileList) {
      if (item.type === 'folder' && item.children) {
        // Search recursively in children
        const found = this.findFolderByNameAndPath(item.children, folderName, item.parentPath || '');
        if (found) return found;
      }
    }

    return null;
  }

  /**
   * Get folder size (sum of all child file sizes)
   * Returns a formatted string like "1.2MB"
   */
  getFolderSize(folder: FileItem): string {
    if (!folder.children || folder.children.length === 0) {
      return '0KB';
    }

    // Calculate total size in bytes (using simple estimation since we have formatted strings)
    let totalSize = 0;
    const collectSizes = (items: FileItem[]) => {
      items.forEach(item => {
        if (item.type === 'file' || item.type === 'image') {
          // Convert size string to bytes (approximate)
          if (item.size) {
            const size = parseFloat(item.size);
            if (item.size.includes('MB')) {
              totalSize += size * 1024 * 1024;
            } else if (item.size.includes('KB')) {
              totalSize += size * 1024;
            } else {
              totalSize += size;
            }
          }
        } else if (item.type === 'folder' && item.children) {
          collectSizes(item.children);
        }
      });
    };

    collectSizes(folder.children);

    // Format the size back to a readable string
    if (totalSize > 1024 * 4) {
      return `${(totalSize / (1024 * 1024)).toFixed(1)}MB`;
    } else if (totalSize > 1024) {
      return `${(totalSize / 1024).toFixed(1)}KB`;
    } else {
      return `${totalSize}B`;
    }
  }

  /**
   * Get count of files in a folder (recursive)
   * Modified to only count selected files and respect already transferred status
   */
  getFolderFileCount(folder: FileItem): number {
    if (!folder.children || folder.children.length === 0) {
      return 0;
    }

    let count = 0;
    const countFiles = (items: FileItem[]) => {
      items.forEach(item => {
        if ((item.type === 'file' || item.type === 'image') && item.selected) {
          count++;
        } else if (item.type === 'folder' && item.children) {
          countFiles(item.children);
        }
      });
    };

    // Find the actual folder in source data to ensure we're counting the latest state
    const actualFolder = this.findFolderInSource(folder.name, folder.parentFolder || '');
    if (actualFolder && actualFolder.children) {
      countFiles(actualFolder.children);
    } else if (folder.children) {
      // Fallback to the provided folder if we can't find the source folder
      countFiles(folder.children);
    }

    return count;
  }

  /**
   * Get count of subfolders in a folder (recursive)
   * Modified to only count selected subfolders
   */
  getFolderSubfolderCount(folder: FileItem): number {
    if (!folder.children || folder.children.length === 0) {
      return 0;
    }

    let count = 0;
    const countSubfolders = (items: FileItem[]) => {
      items.forEach(item => {
        if (item.type === 'folder' && item.selected) {
          count++;
          // Also count nested subfolders if they're selected
          if (item.children) {
            countSubfolders(item.children);
          }
        }
      });
    };

    // Find the actual folder in source data to ensure we're counting the latest state
    const actualFolder = this.findFolderInSource(folder.name, folder.parentFolder || '');
    if (actualFolder && actualFolder.children) {
      countSubfolders(actualFolder.children);
    } else if (folder.children) {
      // Fallback to the provided folder if we can't find the source folder
      countSubfolders(folder.children);
    }

    return count;
  }

  /**
   * Get a formatted summary of folder contents
   * Returns a string like "10 files, 3 subfolders"
   */
  getFolderSummary(folder: FileItem): string {
    const fileCount = this.getFolderFileCount(folder);
    const subfolderCount = this.getFolderSubfolderCount(folder);

    let summary = '';
    if (fileCount > 0) {
      summary += `${fileCount} file${fileCount !== 1 ? 's' : ''}`;
    }
    if (summary) summary += ', ';
    summary += `${subfolderCount} subfolder${subfolderCount !== 1 ? 's' : ''}`;

    return summary || 'Empty folder';
  }

  /**
   * Helper method to remove all files from a folder and its subfolders from the selected files list
   */
  removeFilesFromFolder(folder: FileItem): void {
    if (!folder || !folder.children) return;

    // First remove immediate children
    this.selectedFiles = this.selectedFiles.filter(item => item.parentFolder !== folder.name);

    // Then recursively process subfolders
    folder.children.forEach(child => {
      if (child.type === 'folder') {
        this.removeFilesFromFolder(child);
      }
    });
  }

  /**
   * Returns true if the given folder is selected in the left panel.
   */
  isFolderSelectedInLeftPanel(folder: FileItem): boolean {
    return this.selectedFiles.some(item => item.type === 'folder' && item.name === folder.name);
  }

  /**
   * Find a folder in the source data structure
   */
  findFolderInSource(folderName: string, parentPath: string): FileItem | null {
    // Check in flattened items first for better performance
    const flatFolder = this.flattenedItems.find(
      item => item.type === 'folder' && item.name === folderName && (item.parentPath || '') === parentPath,
    );

    if (flatFolder) {
      return flatFolder;
    }

    // Fallback to search in the original data structure
    if (parentPath === '') {
      // It's a root level folder
      return this.clioFiles.find(item => item.type === 'folder' && item.name === folderName) || null;
    } else {
      // It's a nested folder, use the helper method
      return this.findFolderByNameAndPath(this.clioFiles, folderName, parentPath);
    }
  }

  /**
   * Get count of selected folders
   */
  getSelectedFoldersCount(): number {
    return this.selectedFiles.filter(item => item.type === 'folder').length;
  }

  /**
   * Get count of selected files (excluding folders)
   */
  getSelectedFilesCount(): number {
    return this.selectedFiles.filter(item => item.type !== 'folder').length;
  }

  /**
   * Get common parent folder for selected items, if any
   * Returns the folder path or null if items are from different folders
   */
  getCommonParentFolder(): string | null {
    if (this.selectedFiles.length === 0) return null;

    // Get all parent folders
    const parentFolders = this.selectedFiles.filter(item => item.parentFolder).map(item => item.parentFolder);

    // If no parent folders, items are in root
    if (parentFolders.length === 0) return 'Root';

    // Check if all items are from the same parent folder
    const firstParent = parentFolders[0];
    const allSameParent = parentFolders.every(folder => folder === firstParent);

    if (allSameParent) {
      return firstParent;
    } else {
      return 'Multiple folders';
    }
  }

  /**
   * Updates selection state of all children in the flattened items array
   * This ensures visual consistency in the tree view
   * Modified to respect alreadyTransferred property and handle nested children
   */
  private updateChildrenInFlattenedItems(folder: FileItem, selected: boolean): void {
    // Get the full path of this folder to find its children
    const folderPath = folder.parentPath ? `${folder.parentPath}/${folder.name}` : folder.name;
    console.log(
      `Updating children in flattened items for folder: ${folder.name}, path: ${folderPath}, selected: ${selected}`,
    );

    // Create a set of all paths that should be affected
    const affectedPaths = new Set<string>();
    affectedPaths.add(folderPath);

    // Recursively add all nested folder paths
    const addNestedPaths = (basePath: string) => {
      this.flattenedItems.forEach(item => {
        if (item.parentPath === basePath && item.type === 'folder') {
          const itemPath = basePath ? `${basePath}/${item.name}` : item.name;
          affectedPaths.add(itemPath);
          addNestedPaths(itemPath);
        }
      });
    };

    // Build the complete set of affected paths
    addNestedPaths(folderPath);

    console.log(`Affected paths: ${Array.from(affectedPaths).join(', ')}`);

    // Update all items with matching parentPath
    this.flattenedItems.forEach(item => {
      // If this item's parent path is in the affected paths set
      if (affectedPaths.has(item.parentPath || '') && !this.isFileAlreadyTransferred(item)) {
        // Update the selection state
        if (item.selected !== selected) {
          console.log(`  Updating item ${item.name} selection to ${selected}`);
          item.selected = selected;
        }
      }
    });

    // Force a refresh of the flattened items array to ensure UI consistency
    // This is important for Angular's change detection to pick up the changes
    this.flattenedItems = [...this.flattenedItems];
  }

  /**
   * Check if a file is already transferred
   * This is used to disable checkboxes for already transferred files
   * Enhanced to handle folder checking more thoroughly
   */
  isFileAlreadyTransferred(file: FileItem): boolean {
    if (!file) return false;

    // Direct check if the file has the property
    if (file.alreadyTransferred === true) return true;

    // Check if any parent folder is marked as transferred
    if (file.parentPath) {
      // Split the path to get all parent folders
      const pathParts = file.parentPath.split('/');
      let currentPath = '';

      // Check each parent folder in the path
      for (const part of pathParts) {
        if (!part) continue;

        // Build the path incrementally
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        // Try to find this parent folder
        const parentFolder = this.findFolderByPath(
          currentPath === part ? '' : currentPath.substring(0, currentPath.lastIndexOf('/')),
          part,
        );

        // If any parent folder is transferred, this file is considered transferred
        if (parentFolder && parentFolder.alreadyTransferred === true) {
          return true;
        }
      }
    }

    // New check for clioId matching
    if (file.type === 'file' && file.clioId) {
      return this.isAlreadyTransferredByClioId(file);
    }

    // For files, check if this file exists in the NuageDx files
    if (file.type === 'file' || file.type === 'image') {
      for (const folder of this.nuageDxFiles) {
        if (folder.children && folder.children.some(f => f.name === file.name)) {
          return true;
        }

        // Also check subfolders recursively
        if (folder.children) {
          const hasTransferred = this.checkChildrenForFile(folder.children, file.name);
          if (hasTransferred) return true;
        }
      }
    }

    // For folders, check if the folder exists in NuageDx with the same name
    if (file.type === 'folder') {
      return this.nuageDxFiles.some(f => f.type === 'folder' && f.name === file.name && f.alreadyTransferred === true);
    }

    return false;
  }

  /**
   * For folders, get the count of already transferred files inside them
   */
  getTransferredFileCount(folder: FileItem): number {
    if (!folder.children || folder.children.length === 0) {
      return 0;
    }

    let count = 0;
    const countTransferredFiles = (items: FileItem[]) => {
      items.forEach(item => {
        if ((item.type === 'file' || item.type === 'image') && item.alreadyTransferred) {
          count++;
        } else if (item.type === 'folder' && item.children) {
          countTransferredFiles(item.children);
        }
      });
    };

    countTransferredFiles(folder.children);
    return count;
  }

  /**
   * Recursively update selection state of all children and add them to selected files
   * Modified to respect alreadyTransferred property and not add folders to selectedFiles
   */
  updateChildrenSelectionState(folder: FileItem, selected: boolean): void {
    if (!folder.children) return;

    // First, explicitly set the selection state on all children
    folder.children.forEach(child => {
      // Skip already transferred items when selecting
      if (!this.isFileAlreadyTransferred(child)) {
        // Update the visual selection state
        child.selected = selected;

        // For files, add/remove them to/from selectedFiles
        if (child.type === 'file' || child.type === 'image') {
          if (selected) {
            this.addFileToSelectedList(child, folder.name);
          } else {
            // When deselecting, remove the file from selectedFiles
            this.selectedFiles = this.selectedFiles.filter(
              item => !(item.name === child.name && item.parentFolder === folder.name),
            );
          }
        }

        // For folders, process them and their children recursively
        if (child.type === 'folder' && child.children) {
          // Continue recursively processing this subfolder's children
          this.updateChildrenSelectionState(child, selected);
        }
      }
    });

    // Update folder counts in UI
    if (selected) {
      this.forceUpdateFolderInSelectedFiles(folder.name);
    }

    // Also update all children in the flattened items array for visual consistency
    const folderPath = folder.parentPath ? `${folder.parentPath}/${folder.name}` : folder.name;

    // Find all items that have this folder as parent and update their selection state
    this.flattenedItems.forEach(item => {
      if (item.parentPath === folderPath && !this.isFileAlreadyTransferred(item)) {
        item.selected = selected;
      }
    });
  }

  /**
   * Get a formatted summary of selected files and subfolders in a folder
   * Returns a string like "3 files, 2 subfolders selected"
   */
  getSelectedFolderSummary(folder: FileItem): string {
    // Force recounting of files and subfolders to ensure accuracy
    let fileCount = 0;
    let subfolderCount = 0;

    // Use cached counts if available for better performance,
    // otherwise recalculate them
    if (folder._fileCount !== undefined) {
      fileCount = folder._fileCount;
    } else {
      fileCount = this.getFolderFileCount(folder);
    }

    if (folder._subfolderCount !== undefined) {
      subfolderCount = folder._subfolderCount;
    } else {
      subfolderCount = this.getFolderSubfolderCount(folder);
    }

    let summary = '';
    if (fileCount > 0) {
      summary += `${fileCount} file${fileCount !== 1 ? 's' : ''}`;
    }
    if (subfolderCount > 0) {
      if (summary) summary += ', ';
      summary += `${subfolderCount} subfolder${subfolderCount !== 1 ? 's' : ''}`;
    }

    return summary ? `${summary} selected` : 'No items selected';
  }

  /**
   * Gets the full path segments for a file
   * Extracts all folders from a path string, handling different separators
   */
  getFullPathSegments(parentFolder: string | undefined): string[] {
    if (!parentFolder || parentFolder === 'Root') {
      return [];
    }

    // Split the path by common separators (/ or \) and filter out empty segments
    return parentFolder.split(/[\/\\]/).filter(segment => segment.trim() !== '');
  }

  /**
   * Forces an update of a folder in the selectedFiles array by creating a fresh copy
   * This ensures Angular's change detection will update the folder subtitle counts in the UI
   */
  forceUpdateFolderInSelectedFiles(folderName: string): void {
    // Find the folder in selectedFiles
    const folderIndex = this.selectedFiles.findIndex(item => item.type === 'folder' && item.name === folderName);

    if (folderIndex !== -1) {
      // Get a reference to the actual source folder to ensure we have latest data
      const sourceFolder = this.findFolderInSource(folderName, '');

      if (sourceFolder) {
        // Create a new folder object with updated count properties
        const updatedFolder = {
          ...this.selectedFiles[folderIndex],
          // Update timestamp to force Angular change detection
          _lastCountRefreshTime: Date.now(),
          // Pre-calculate counts for instant UI display
          _fileCount: this.getFolderFileCount(sourceFolder),
          _subfolderCount: this.getFolderSubfolderCount(sourceFolder),
        };

        // Replace the folder in the array with the updated one
        this.selectedFiles[folderIndex] = updatedFolder;

        // For complex changes, we can force a full array update
        if (updatedFolder._fileCount === 0 && updatedFolder._subfolderCount === 0) {
          // If all items are unselected, use a brand new array reference
          this.selectedFiles = [...this.selectedFiles];
        }
      }
    }
  }

  /**
   * Specifically updates parent folder in the selectedFiles list when a file's selected state changes
   * This is critical for updating folder subtitles in the right panel when files are selected/unselected
   */
  forceUpdateParentFolderInSelectedFiles(folderName: string): void {
    // Find the folder in selectedFiles
    const parentFolderIndex = this.selectedFiles.findIndex(item => item.type === 'folder' && item.name === folderName);

    if (parentFolderIndex !== -1) {
      // Get actual folder from source to ensure latest selection state
      const sourceFolder = this.findFolderInSource(folderName, '');

      if (sourceFolder) {
        // Calculate current counts for selected files/subfolders
        const fileCount = this.getFolderFileCount(sourceFolder);
        const subfolderCount = this.getFolderSubfolderCount(sourceFolder);

        // Create a completely new object to force Angular change detection
        const updatedFolder = {
          ...this.selectedFiles[parentFolderIndex],
          _lastCountRefreshTime: Date.now(),
          _fileCount: fileCount,
          _subfolderCount: subfolderCount,
        };

        // Replace the folder with the updated copy
        this.selectedFiles[parentFolderIndex] = updatedFolder;

        // If there are no more selected items in this folder, update the whole array
        // This ensures proper UI updates for empty folders
        if (fileCount === 0 && subfolderCount === 0) {
          this.selectedFiles = [...this.selectedFiles];
        }

        console.log(`Updated folder: ${folderName}, files: ${fileCount}, subfolders: ${subfolderCount}`);
      }
    }
  }

  /**
   * Refreshes all folder references in the selectedFiles array
   * to ensure folder counts are updated in the UI
   * Optimized to create fewer new objects for better performance
   */
  refreshAllFolderReferences(): void {
    // Track if we need to update the entire array
    let folderUpdated = false;

    // Create a new array with refreshed folder references
    const updatedSelectedFiles = this.selectedFiles.map(item => {
      if (item.type === 'folder') {
        // Create a new reference for folders to trigger Angular change detection
        folderUpdated = true;

        // Create a new reference with special attention to ensure counts will update
        const updatedFolder = { ...item };

        // Find the source folder to get accurate counts
        const sourceFolder = this.findFolderInSource(item.name, item.parentFolder || '');
        if (sourceFolder) {
          // Pre-calculate the counts to ensure they're refreshed
          updatedFolder._fileCount = this.getFolderFileCount(sourceFolder);
          updatedFolder._subfolderCount = this.getFolderSubfolderCount(sourceFolder);
        }

        return updatedFolder;
      }
      return item;
    });

    // Only create a new array reference if folders were actually updated
    if (folderUpdated) {
      // Force a refresh of the entire selectedFiles array by creating a new reference
      this.selectedFiles = updatedSelectedFiles;
    }
  }

  /**
   * Used as a trackBy function to force Angular to regenerate the folder summary
   * when files inside a folder are selected or unselected
   */
  trackFolderChanges(folder: FileItem): string {
    // Create a unique tracking key that changes when folder content selection changes
    // Use the stored counts when available to avoid unnecessary recalculation
    const fileCount = folder._fileCount !== undefined ? folder._fileCount : this.getFolderFileCount(folder);
    const subfolderCount =
      folder._subfolderCount !== undefined ? folder._subfolderCount : this.getFolderSubfolderCount(folder);

    // Include the timestamp to force rendering updates
    return `${folder.name}_files_${fileCount}_folders_${subfolderCount}_${folder._lastCountRefreshTime || Date.now()}`;
  }

  /**
   * Simulates or performs an async operation to load folder contents
   * In a real implementation, this would call an API
   */
  private async fetchFolderContents(folder: FileItem): Promise<any> {
    // Get the folder contents from the API
    let folderContents = await this.getFolderContents(folder.clioId);

    // If we have the matterLocalClioFiles, filter out already transferred files
    if (folderContents && this.matterLocalClioFiles) {
      // Filter out already transferred files instead of just marking them
      folderContents = this.filterTransferredFiles(folderContents);
    }

    return folderContents;
  }

  /**
   * Filters out files that have already been transferred to NuageDx
   * Returns a new array with transferred files removed
   */
  private filterTransferredFiles(files: FileItem[]): FileItem[] {
    if (!this.matterLocalClioFiles || !Array.isArray(this.matterLocalClioFiles)) {
      return files;
    }

    // Create a recursive function to filter out transferred files
    const filterFiles = (items: FileItem[]): FileItem[] => {
      if (!items || !Array.isArray(items)) return [];

      return items.filter(file => {
        // For files, check if already transferred
        if (file.type === 'file' && file.clioId) {
          // If file is already transferred, remove it by not including in filter results
          const isTransferred = this.isAlreadyTransferredByClioId(file);
          if (isTransferred) {
            console.log(`Filtered out already transferred file: ${file.name} (ID: ${file.clioId})`);
            return false; // Remove this file
          }
        }

        // For folders, recursively filter their children
        if (file.type === 'folder' && file.children && Array.isArray(file.children)) {
          // Apply filtering to children
          file.children = filterFiles(file.children);

          // Always keep folders, even if they're empty after filtering
          return true;
        }

        return true; // Keep this item
      });
    };

    // Apply filtering and return the filtered array
    return filterFiles(files);
  }

  /**
   * Marks files in the given contents as already transferred
   * This is similar to markTransferredFiles but works on a specific set of contents
   */
  private markTransferredInContents(contents: FileItem[]): void {
    if (!this.matterLocalClioFiles || !Array.isArray(this.matterLocalClioFiles)) {
      return;
    }

    const checkFiles = (files: FileItem[], parentIsTransferred: boolean = false): void => {
      if (!files || !Array.isArray(files)) return;

      files.forEach(file => {
        // If parent folder is marked as transferred, inherit that status
        if (parentIsTransferred) {
          file.alreadyTransferred = true;
          file.transferredClass = 'already-transferred';
          file.accessibilityLabel = 'Already transferred';
          file.hasCheckmark = true;
        }
        // Otherwise check if the file itself is transferred
        else if (file.type === 'file' && file.clioId) {
          // Check if this file's clioId matches any clioDocId in matterLocalClioFiles
          file.alreadyTransferred = this.isAlreadyTransferredByClioId(file);

          // Add CSS class and accessibility properties
          if (file.alreadyTransferred) {
            file.transferredClass = 'already-transferred';
            file.accessibilityLabel = 'Already transferred';
            file.hasCheckmark = true;
            console.log(`Marked newly loaded file as already transferred: ${file.name} (ID: ${file.clioId})`);
          }
        }

        // If it's a folder, recursively check its children
        if (file.type === 'folder' && file.children && Array.isArray(file.children)) {
          // Pass down the transferred status to all children
          // Children inherit the transferred status if either the parent folder is transferred
          // or if the current folder itself is transferred
          checkFiles(file.children, parentIsTransferred || file.alreadyTransferred === true);
        }
      });
    };

    // Check all files in the contents, starting with no parent transferred status
    checkFiles(contents, false);
  }

  /**
   * Recursively checks if a file exists in a list of children files/folders
   * @param children The list of file items to check
   * @param fileName The name of the file to search for
   * @returns true if the file is found, false otherwise
   */
  checkChildrenForFile(children: FileItem[], fileName: string): boolean {
    if (!children || !Array.isArray(children)) return false;

    for (const child of children) {
      // Check if this child matches the file name
      if ((child.type === 'file' || 'image') && child.name === fileName) {
        return true;
      }

      // If it's a folder, check its children recursively
      if (child.type === 'folder' && child.children) {
        const foundInChild = this.checkChildrenForFile(child.children, fileName);
        if (foundInChild) return true;
      }
    }

    // File not found in any child
    return false;
  }

  /**
   * Synchronizes selection states between the UI and the selectedFiles array after folder navigation
   * This ensures that files remain visually selected when moving between folders
   */
  syncSelectionStatesAfterNavigation(): void {
    // First, create a map of selected files using a unique identifier
    const selectedMap = new Map<string, boolean>();

    this.selectedFiles.forEach(file => {
      const parentKeyPart = file.parentFolder === 'Root' || !file.parentFolder ? '' : file.parentFolder;
      const key = `${file.name}|${parentKeyPart}|${file.type}`;
      selectedMap.set(key, true);
    });

    // Helper: recursively select all children if parent folder is selected
    const propagateSelectionToChildren = (folder: FileItem) => {
      if (!folder.children) return;
      folder.children.forEach(child => {
        if (!this.isFileAlreadyTransferred(child)) {
          child.selected = true;
          // If child is a folder and not already transferred, propagate recursively
          if (child.type === 'folder') {
            propagateSelectionToChildren(child);
          }
        }
      });
    };

    // Update the selection state in the actual data structure (clioFiles)
    this.updateSelectionInDataStructure(this.clioFiles, selectedMap);

    // For each selected folder, ensure all its children are visually selected
    this.selectedFiles.forEach(file => {
      if (file.type === 'folder') {
        // Find the actual folder in the source structure
        const folder = this.findFolderInSource(file.name, file.parentFolder || '');
        if (folder) {
          propagateSelectionToChildren(folder);
        }
      }
    });

    // Update visible items in the current folder
    this.currentFolderContents.forEach(item => {
      const currentFolder = this.getCurrentFolder();
      const parentPath = currentFolder ? currentFolder.name : '';
      const key = `${item.name}|${parentPath}|${item.type}`;
      item.selected = selectedMap.has(key) || (currentFolder && currentFolder.selected);
    });

    // Update all flattened items to ensure visual consistency throughout the tree
    this.flattenedItems.forEach(item => {
      const parentPath = item.parentPath || '';
      const parentFolderName = parentPath.split('/').pop() || '';
      const key = `${item.name}|${parentFolderName}|${item.type}`;
      // If parent folder is selected, mark as selected
      if (selectedMap.has(key)) {
        item.selected = true;
      } else {
        // Check if any ancestor folder is selected
        let ancestorPath = parentPath;
        let ancestorSelected = false;
        while (ancestorPath) {
          const ancestorName = ancestorPath.split('/').pop() || '';
          const ancestorKey = `${ancestorName}|${ancestorPath.substring(0, ancestorPath.lastIndexOf('/'))}|folder`;
          if (selectedMap.has(ancestorKey)) {
            ancestorSelected = true;
            break;
          }
          ancestorPath = ancestorPath.substring(0, ancestorPath.lastIndexOf('/'));
        }
        item.selected = ancestorSelected;
      }
    });

    // Important: Update all folder selection states to reflect their children's selection
    this.updateAllFolderSelectionStates();
  }

  /**
   * Recursively updates selection state in the original data structure
   * This ensures that when folders are expanded/collapsed, their items maintain selection state
   */
  private updateSelectionInDataStructure(items: FileItem[], selectedMap: Map<string, boolean>): void {
    if (!items) return;

    items.forEach(item => {
      // Get the parent folder name
      let parentFolderName = '';
      if (item.parentPath) {
        const pathParts = item.parentPath.split('/');
        parentFolderName = pathParts[pathParts.length - 1] || '';
      }

      // Check if this item is in the selected files map
      const key = `${item.name}|${parentFolderName}|${item.type}`;
      // const rootKey = `${item.name}||${item.type}`; // Not strictly needed if key normalization is correct

      if (item.parentPath === '' || item.parentPath === undefined) {
        // This condition is for root items
        // With normalized keys, selectedMap.has(key) should now work correctly for root items.
        // The existing complex if/else if/else for root items can be simplified if desired,
        // or kept, as the core issue was the map lookup.
        // For instance, the original simpler logic would now work:
        // item.selected = selectedMap.has(key);

        // Current logic:
        if ((item.type === 'file' || item.type === 'image') && !item.selected) {
          const isInSelectedFiles = this.selectedFiles.some(
            f =>
              f.name === item.name &&
              (f.parentFolder === 'Root' || !f.parentFolder) && // Adjusted condition for selectedFiles
              f.type === item.type,
          );
          if (isInSelectedFiles) {
            console.log(`Forced selection of root file: ${item.name}`);
            item.selected = true;
          } else {
            // If it's a root item and NOT in selectedMap (after key normalization), it should be unselected.
            item.selected = selectedMap.has(key);
          }
        } else if (item.selected && (item.type === 'file' || item.type === 'image')) {
          item.selected = selectedMap.has(key);
        } else if (item.type === 'folder') {
          item.selected = selectedMap.has(key);
        } else {
          // Fallback for any other root item case not explicitly handled by above conditions
          item.selected = selectedMap.has(key);
        }
      } else {
        // Not a root item
        item.selected = selectedMap.has(key);
      }

      // Recursively process children for folders
      if (item.type === 'folder' && item.children) {
        this.updateSelectionInDataStructure(item.children, selectedMap);

        // After processing all children, update folder selection state
        // based on children's states to handle indeterminate state properly
        if (item.children.length > 0) {
          const allSelected = item.children.every(child => child.selected);
          const noneSelected = item.children.every(child => !child.selected);

          // If folder itself is not directly selected but some children are,
          // we need to preserve the indeterminate visual state
          if (!allSelected && !noneSelected && !item.selected) {
            // Don't set selected=true, as this would add the folder to selectedFiles
            // The indeterminate state in UI is controlled by areSomeFolderFilesSelected()
            console.log(`Folder ${item.name} has some children selected but not all`);
          }
        }
      }
    });
  }

  /**
   * Parses a full path into segments for display
   * @param path The full path string (e.g. "Root/Folder1/Folder2")
   * @returns Array of path segments with names and isLast flag
   */
  parsePathForDisplay(path: string): { name: string; isLast: boolean }[] {
    if (!path || path === 'Root') {
      return [{ name: 'Root', isLast: true }];
    }

    // Split by '/' and filter out empty segments
    const segments = path.split('/').filter(segment => segment.trim() !== '');

    // Map segments to objects with name and isLast flag
    return segments.map((segment, index) => ({
      name: segment,
      isLast: index === segments.length - 1,
    }));
  }

  /**
   * Navigate to a nested folder by folder name path
   * @param folderPath Array of folder names to navigate through
   * @returns Promise that resolves when navigation is complete
   */
  async navigateToNestedFolder(folderPath: string[]): Promise<void> {
    // Start from root
    let currentItems = this.clioFiles;
    let currentFolder: FileItem | null = null;

    // Reset breadcrumbs to root
    this.breadcrumbs = [{ name: 'Home', folder: null }];

    // Navigate through the folder path
    for (const folderName of folderPath) {
      // Find the folder at this level
      const folder = currentItems.find(item => item.type === 'folder' && item.name === folderName);

      if (folder) {
        console.log(`Found folder: ${folderName}`);

        // Load folder contents if they're not already loaded
        if (!folder.children || folder.children.length === 0) {
          try {
            // Set loading state
            this.loadingFolderId = folder.name;

            // Fetch folder contents
            const contents = await this.fetchFolderContents(folder);
            if (contents) {
              folder.children = contents;
            }
          } catch (error) {
            console.error(`Error fetching contents for folder ${folder.name}:`, error);
          } finally {
            this.loadingFolderId = null;
          }
        }

        // Set folder as expanded and close all other folders
        this.closeAllFolders(folder);
        folder.expanded = true;

        // Add to breadcrumbs
        this.breadcrumbs.push({ name: folder.name, folder });

        // Update current folder
        currentFolder = folder;

        // Update current items for next iteration
        if (folder.children) {
          currentItems = folder.children;
        } else {
          // Break if no more children
          break;
        }
      } else {
        console.log(`Folder not found: ${folderName}`);
        break;
      }
    }

    // If we found the target folder, make it the active folder
    if (currentFolder) {
      // Update current folder contents
      this.currentFolderContents = currentFolder.children || [];

      // Update flattened items and visibility
      this.updateFlattenedItems();
      this.updateItemVisibility();

      // Clear any active search when navigating
      this.clearSearch();

      // Ensure selection states remain synchronized
      this.syncSelectionStatesAfterNavigation();

      console.log(`Navigated to nested folder: ${currentFolder.name}`);
    } else {
      console.log('Failed to navigate to folder path:', folderPath);
      // If navigation failed, fall back to root
      await this.navigateToBreadcrumb(0);
    }
  }

  /**
   * Updates a file's selection state in the flattened items array
   * This ensures visual consistency between the current view and selectedFiles
   */
  updateFileInFlattenedItems(file: FileItem, parentFolderName: string | null): void {
    // Find the file in the flattened items array - ensure we match by path as well as name
    const flatItem = this.flattenedItems.find(
      item =>
        item.name === file.name &&
        item.type === file.type &&
        (item.parentPath ? item.parentPath.split('/').pop() : '') === (parentFolderName || '') &&
        // If we have size and date, match those too for additional accuracy
        ((!file.size && !item.size) || item.size === file.size) &&
        ((!file.date && !item.date) || item.date === file.date),
    );

    // Update its selected state if found
    if (flatItem) {
      flatItem.selected = file.selected;
    }
  }

  /**
   * Finds and highlights/selects a file in the left panel after navigation
   * @param file The file object to highlight
   */
  highlightFileInLeftPanel(file: FileItem): void {
    if (!file) return;

    // Find the file in the current folder contents
    const fileInLeftPanel = this.currentFolderContents.find(
      item => item.name === file.name && item.type === file.type && (!item.size || item.size === file.size),
    );

    if (fileInLeftPanel) {
      console.log('Found file in left panel:', fileInLeftPanel.name);

      // Wait for next render cycle to ensure DOM is updated
      setTimeout(() => {
        // Try to find the element in the DOM
        // Note: this is a simplified selector, may need adjustment based on actual DOM structure
        const fileElement =
          document.querySelector(`.tree-node .file-name:contains('${file.name}')`) ||
          document.querySelector(`.tree-node:contains('${file.name}')`);

        if (fileElement) {
          // Scroll the element into view
          fileElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Add highlight class to parent tree node
          const treeNode = fileElement.closest('.tree-node');
          if (treeNode) {
            // Add highlight flash animation class
            treeNode.classList.add('highlight-flash');

            // Remove highlight after animation completes
            setTimeout(() => {
              treeNode.classList.remove('highlight-flash');
            }, 1500); // Duration of flash animation
          }
        }
      }, 100); // Small delay to ensure DOM is ready
    } else {
      console.log('File not found in current folder contents:', file.name);
    }
  }

  /**
   * Map search results from the API to our FileItem format
   */
  private mapSearchResultsToFileItems(searchResults: any[]): FileItem[] {
    if (!searchResults || !Array.isArray(searchResults)) {
      console.warn('Invalid search results format:', searchResults);
      return [];
    }

    return searchResults.map(item => {
      // Determine if file is an image based on filename extension
      const fileExtension = item.filename?.split('.').pop()?.toLowerCase();
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtension);

      // Determine the parent folder name based on parent data
      let parentFolderName = 'Root';
      if (item.parent) {
        // If parent has root property and it's false, use the parent name
        // Otherwise if root is true, use 'Root'
        parentFolderName = item.parent.root === false && item.parent.name ? item.parent.name : 'Root';
      }

      const fileItem: FileItem = {
        name: item.filename || 'Unnamed File',
        type: item.type === 'Folder' ? 'folder' : isImage ? 'image' : 'file',
        clioId: item.id?.toString() || '',
        date: item.updated_at
          ? new Date(item.updated_at).toLocaleDateString()
          : item.created_at
            ? new Date(item.created_at).toLocaleDateString()
            : new Date().toLocaleDateString(),
        selected: false,
        expanded: false,
        visible: true,
        parentPath: item.parent?.name || '', // Use parent name instead of ID
        parentFolder: parentFolderName,
      };

      // Add accessibility properties if already transferred
      if (this.isAlreadyTransferredByClioId(fileItem)) {
        fileItem.alreadyTransferred = true;
        fileItem.transferredClass = 'already-transferred';
        fileItem.accessibilityLabel = 'Already transferred';
        fileItem.hasCheckmark = true;
      }

      return fileItem;
    });
  }

  /**
   * Close sibling folders of a specified folder to avoid crowding the UI
   * This keeps the clicked folder expanded while collapsing others in the same level
   */
  closeSiblingFolders(folder: FileItem): void {
    console.log(`Closing sibling folders of: ${folder.name}`);

    // Get the folder's path - will be something like '/parent/currentfolder'
    const sourcePath = folder.parentPath || '';

    const closeInSource = (items: FileItem[], sourcePath: string): void => {
      // Find items with the same parentPath (siblings)
      for (const item of items) {
        if (
          item.type === 'folder' &&
          item.parentPath === sourcePath && // Same parent path
          item !== folder && // Not the folder we want to keep expanded
          item.expanded // Currently expanded
        ) {
          // Close this sibling folder
          item.expanded = false;
          console.log(`  Closed sibling folder: ${item.name}`);
        }

        // Recursively check all children regardless
        if (item.type === 'folder' && item.children) {
          closeInSource(item.children, sourcePath);
        }
      }
    };

    // Start checking from the root items
    closeInSource(this.clioFiles, sourcePath);
  }

  /**
   * Organize files and folders into a proper hierarchy
   * Items with parentFolder matching another item's id will be nested under that parent
   */
  organizeFileHierarchy(files: FileItem[]): FileItem[] {
    // Create maps for looking up items by different properties
    const itemsByIdMap = new Map<string, FileItem>();
    const itemsByClioIdMap = new Map<string, FileItem>();
    const itemsByNameMap = new Map<string, FileItem>();

    // Clone the array to avoid modifying the original during restructuring
    const clonedFiles = [...files];

    console.log('Organizing file hierarchy for:', clonedFiles);

    // Populate the maps with all items
    clonedFiles.forEach(file => {
      // Map by ID (most important for matching)
      if (file.id) {
        itemsByIdMap.set(file.id, file);
      }

      // Also map by clioId as backup
      if (file.clioId) {
        if (typeof file.clioId === 'string') {
          itemsByClioIdMap.set(file.clioId, file);
        } else {
          // Handle numeric clioId by converting to string
          itemsByClioIdMap.set(String(file.clioId), file);
        }
      }

      // Map by name for folders (as last resort)
      if (file.type === 'folder' && !itemsByNameMap.has(file.name)) {
        itemsByNameMap.set(file.name, file);
      }
    });

    // Second pass: organize children under their parents
    const rootItems: FileItem[] = [];
    const processedItems = new Set<FileItem>(); // Track which items have been processed

    clonedFiles.forEach(file => {
      // Skip if already processed as a child
      if (processedItems.has(file)) {
        return;
      }

      // Primary case: parentFolder matches another file's id
      if (file.parentFolder && itemsByIdMap.has(file.parentFolder)) {
        const parent = itemsByIdMap.get(file.parentFolder);

        // Initialize parent's children array if it doesn't exist
        if (parent && !parent.children) {
          parent.children = [];
        }

        // Add the file to its parent's children
        if (parent && parent.children) {
          parent.children.push(file);
          processedItems.add(file);
          console.log(`Added ${file.name} to parent ${parent.name} by ID match`);
        }
      }
      // Case 2: Check if parentFolder matches clioId (fallback)
      else if (file.parentFolder && itemsByClioIdMap.has(file.parentFolder)) {
        const parent = itemsByClioIdMap.get(file.parentFolder);

        // Initialize parent's children array if it doesn't exist
        if (parent && !parent.children) {
          parent.children = [];
        }

        // Add the file to its parent's children
        if (parent && parent.children) {
          parent.children.push(file);
          processedItems.add(file);
          console.log(`Added ${file.name} to parent ${parent.name} by clioId match`);
        }
      }
      // Case 3: If we have parentFolderName, try to match by name
      else if (file.parentFolderName && file.parentFolderName !== 'Clio' && itemsByNameMap.has(file.parentFolderName)) {
        const parent = itemsByNameMap.get(file.parentFolderName);

        // Initialize parent's children array if it doesn't exist
        if (parent && !parent.children) {
          parent.children = [];
        }

        // Add the file to its parent's children
        if (parent && parent.children) {
          parent.children.push(file);
          processedItems.add(file);
          console.log(`Added ${file.name} to parent ${parent.name} by name match`);
        }
      }
      // If none of the above, it's a root item
      else if (!processedItems.has(file)) {
        rootItems.push(file);
        console.log(`Added ${file.name} as root item`);
      }
    });

    // Find any orphaned items and add them to root
    clonedFiles.forEach(file => {
      if (!processedItems.has(file) && !rootItems.includes(file)) {
        rootItems.push(file);
        console.log(`Added orphaned item ${file.name} to root`);
      }
    });

    console.log(`Organized hierarchy: ${rootItems.length} root items, ${processedItems.size} nested items`);

    return rootItems;
  }

  /**
   * Checks if all files in a folder have been transferred
   * @param folder The folder to check
   * @returns true if all files are transferred or if the folder is empty after filtering
   */
  areAllFilesTransferred(folder: FileItem): boolean {
    // If the folder has no children or children array doesn't exist, consider it synced
    if (!folder.children || folder.children.length === 0) {
      return true;
    }

    // Check if all children are transferred
    return folder.children.every(child => {
      if (child.type === 'folder') {
        // Recursive check for nested folders
        return this.areAllFilesTransferred(child);
      } else {
        // For files, check if they're already transferred
        return child.alreadyTransferred === true;
      }
    });
  }

  /**
   * This is the handler for checkbox changes in the right panel (selected files)
   * It directly updates all data structures to ensure both panels stay in sync
   */
  rightPanelCheckboxChange(file: FileItem): void {
    // Update selection state in the source data
    this.updateSourceFileSelectionState(file, !file.selected);

    // Remove the file from selectedFiles if it's being unchecked
    if (!file.selected) {
      this.removeSelectedFile(file);
    } else {
      // If being checked, add to selected files
      this.addFileToSelectedList(file, file.parentFolderName || null);
    }

    // Update folder selection states to reflect changes
    const parentFolder = this.findParentFolder(this.nuageDxFiles, file);
    if (parentFolder) {
      this.updateFolderSelectionState(parentFolder);
    }

    // Sync selection states after changes
    this.syncSelectionStatesAfterNavigation();
  }

  /**
   * Creates a root folder structure with the sourceObjectName and places files inside it
   * @param files The original files array
   * @returns A new array with the root folder structure
   */
  private createRootFolderStructure(files: FileItem[]): FileItem[] {
    if (!this.sourceObjectName || files.length === 0) {
      return files;
    }

    // Create a root folder with the sourceObjectName
    const rootFolder: FileItem = {
      name: this.sourceObjectName,
      type: 'folder',
      expanded: true, // Set to expanded by default
      children: [...files], // Place all files inside this folder
      selected: true, // Select the folder by default
      depth: 0,
      visible: true,
    };

    // Return an array with just the root folder
    return [rootFolder];
  }

  /**
   * Determines if a folder should be considered selected based on its children
   * A folder is considered selected if all non-transferred children are selected
   */
  isFolderSelected(folder: FileItem): boolean {
    if (!folder.children || folder.children.length === 0) {
      // Empty folders use their own selected state
      return folder.selected;
    }

    // Check if all non-transferred children are selected
    const nonTransferredChildren = folder.children.filter(child => !this.isFileAlreadyTransferred(child));

    // If there are no non-transferred children, use the folder's own selected state
    if (nonTransferredChildren.length === 0) {
      return folder.selected;
    }

    // A folder is selected if all its non-transferred children are selected
    return nonTransferredChildren.every(child => child.selected);
  }


  /**
   * Adds all child files of a folder to the selectedFiles array
   * This is used instead of adding the folder itself
   */
  private addFolderChildrenToSelectedFiles(folder: FileItem): void {
    if (!folder.children) return;

    // Process all children recursively
    const processChildren = (items: FileItem[], parentFolder: string) => {
      items.forEach(item => {
        // Skip already transferred items
        if (this.isFileAlreadyTransferred(item)) return;

        // Mark the item as selected
        item.selected = true;

        // For files, add to selectedFiles
        if (item.type === 'file' || item.type === 'image') {
          this.addFileToSelectedList(item, parentFolder);
        }

        // For folders, recursively process their children
        if (item.type === 'folder' && item.children) {
          processChildren(item.children, item.name);
        }
      });
    };

    // Start processing from the folder's children
    processChildren(folder.children, folder.name);
  }

  /**
   * Removes all child files of a folder from the selectedFiles array
   */
  private removeFolderChildrenFromSelectedFiles(folder: FileItem): void {
    if (!folder.children) return;

    // Process all children recursively
    const processChildren = (items: FileItem[], parentFolder: string) => {
      items.forEach(item => {
        // For files, remove from selectedFiles
        if (item.type === 'file' || item.type === 'image') {
          this.selectedFiles = this.selectedFiles.filter(
            f => !(f.name === item.name && (f.parentFolder || '') === parentFolder),
          );
        }

        // For folders, recursively process their children
        if (item.type === 'folder' && item.children) {
          processChildren(item.children, item.name);
        }
      });
    };

    // Start processing from the folder's children
    processChildren(folder.children, folder.name);
  }

  /**
   * Process the selection state of all children in a folder
   * This updates both the UI state and the selectedFiles array
   */
  private processChildrenSelection(folder: FileItem, selected: boolean): void {
    if (!folder.children) return;

    folder.children.forEach(child => {
      // Skip already transferred items
      if (this.isFileAlreadyTransferred(child)) return;

      // Update selection state of the child
      child.selected = selected;

      // For files, add/remove from selectedFiles
      if (child.type === 'file' || child.type === 'image') {
        if (selected) {
          this.addFileToSelectedList(child, folder.name);
        } else {
          // When deselecting, remove the file from selectedFiles
          this.selectedFiles = this.selectedFiles.filter(
            f => !(f.name === child.name && (f.parentFolder || '') === folder.name),
          );
        }
      }

      // For folders, recursively process children without adding the folder itself
      if (child.type === 'folder') {
        // Process this folder's children recursively
        if (child.children) {
          this.processChildrenSelection(child, selected);
        }
      }

      // Update the flattened item representation of this child
      const flattenedChild = this.flattenedItems.find(
        item =>
          item.name === child.name &&
          item.type === child.type &&
          (item.parentPath || '') === (child.parentPath || folder.name),
      );

      if (flattenedChild) {
        flattenedChild.selected = selected;
      }
    });

    // After processing all children, update the folder's selection state
    // based on whether all its children are selected
    if (folder.children.length > 0) {
      const nonTransferredChildren = folder.children.filter(child => !this.isFileAlreadyTransferred(child));
      if (nonTransferredChildren.length > 0) {
        // Update the folder's selected state based on its children
        folder.selected = nonTransferredChildren.every(child => child.selected);
      }
    }
  }

  /**
   * Direct file selection handler that bypasses ngModel binding
   * This is used to handle the checkbox change event directly
   */
  toggleFileSelectionDirect(file: FileItem, checked: boolean): void {
    console.log(`Direct file selection for ${file.name}: ${checked}`);

    // Don't allow selection of already transferred files
    if (file.alreadyTransferred) {
      file.selected = false;
      return;
    }

    // Set the selection state directly
    file.selected = checked;

    // Find the parent folder for proper identification
    const parentFolder = this.findParentFolder(this.clioFiles, file);
    const parentFolderName = parentFolder
      ? parentFolder.name
      : this.breadcrumbs.length > 1
        ? this.breadcrumbs[this.breadcrumbs.length - 1].name
        : 'Root';

    // Check if the parent folder is already selected
    const isParentSelected = this.selectedFiles.some(
      item => item.type === 'folder' && item.name === parentFolderName && !item.parentFolder,
    );

    // When parent folder is selected, just update the UI state
    if (isParentSelected) {
      console.log(
        `File ${file.name} selection toggled, but parent folder is already selected. Only updating UI state.`,
      );

      // Update parent folder's selection state if necessary
      if (parentFolder) {
        this.updateFolderSelectionState(parentFolder);
        this.forceUpdateParentFolderInSelectedFiles(parentFolderName);
      }

      return;
    }

    // Normal handling when parent folder is NOT selected
    if (checked) {
      // Add to selectedFiles if not already there
      this.addFileToSelectedList(file, parentFolderName);
    } else {
      // Remove from selectedFiles
      this.selectedFiles = this.selectedFiles.filter(
        item => !(item.name === file.name && item.parentFolder === parentFolderName),
      );
    }

    // Update parent folder selection state if there is a parent folder
    if (parentFolder) {
      this.updateFolderSelectionState(parentFolder);

      // Check if parent folder is in the selectedFiles list and update its counts
      const parentFolderInSelectedList = this.selectedFiles.find(
        item => item.type === 'folder' && item.name === parentFolderName,
      );

      if (parentFolderInSelectedList) {
        // Force update the folder in the selected files to refresh its subtitle counts
        this.forceUpdateFolderInSelectedFiles(parentFolderName);
        this.forceUpdateParentFolderInSelectedFiles(parentFolderName);
      }
    }

    // Update the file in the flattened items array to ensure tree view consistency
    this.updateFileInFlattenedItems(file, parentFolderName);

    // Update duplicates state
    this.updateDuplicatesState();
  }

  /**
   * Direct handler for right panel checkbox changes
   * This bypasses ngModel binding to avoid potential conflicts
   */
  rightPanelCheckboxChangeDirect(event): void {
    const { file, checked } = event;
    // Directly update the file's selection state
    file.selected = checked;

    if (!checked) {
      // If unchecked, remove from selected files
      this.removeSelectedFile(file);

      // Find and update the corresponding file in the source data
      const updateFileSelection = (items: FileItem[]): boolean => {
        for (const item of items) {
          if (
            item.type === 'file' &&
            item.name === file.name &&
            (item.path === file.path || item.filePath === file.filePath)
          ) {
            // Use the existing method to update file selection
            this.toggleFileSelectionDirect(item, false);
            return true;
          }

          // Recursively check folders
          if (item.type === 'folder' && item.children) {
            if (updateFileSelection(item.children)) {
              return true;
            }
          }
        }
        return false;
      };

      // Try to update in different file lists
      updateFileSelection(this.clioFiles);
      updateFileSelection(this.nuageDxFiles);
      updateFileSelection(this.currentFolderContents);
    } else {
      // If checked, add to selected files
      this.addFileToSelectedList(file, file.parentFolderName || null);

      // Find and update the corresponding file in the source data
      const updateFileSelection = (items: FileItem[]): boolean => {
        for (const item of items) {
          if (
            item.type === 'file' &&
            item.name === file.name &&
            (item.path === file.path || item.filePath === file.filePath)
          ) {
            // Use the existing method to update file selection
            this.toggleFileSelectionDirect(item, true);
            return true;
          }

          // Recursively check folders
          if (item.type === 'folder' && item.children) {
            if (updateFileSelection(item.children)) {
              return true;
            }
          }
        }
        return false;
      };

      // Try to update in different file lists
      updateFileSelection(this.clioFiles);
      updateFileSelection(this.nuageDxFiles);
      updateFileSelection(this.currentFolderContents);
    }

    // Update folder selection states
    const parentFolder = this.findParentFolder(this.nuageDxFiles, file);
    if (parentFolder) {
      this.updateFolderSelectionState(parentFolder);
    }

    // Sync selection states
    this.syncSelectionStatesAfterNavigation();
  }

  /**
   * Filters out already transferred files from the selectedFiles array
   * This ensures transferred files don't appear in the right panel
   */
  private filterTransferredFilesFromSelected(): void {
    // Filter out already transferred files from the selectedFiles array
    this.selectedFiles = this.selectedFiles.filter(file => !this.isFileAlreadyTransferred(file));
  }

  /**
   * Check if a folder has any non-transferred children
   * Used to determine if we should show a checkbox for root folders
   */
  hasFolderNonTransferredChildren(folder: FileItem): boolean {
    if (!folder.children || folder.children.length === 0) {
      return false;
    }

    // Check if any child is not transferred
    return folder.children.some(child => !this.isFileAlreadyTransferred(child));
  }

  // Method to handle unchecking a file
  private handleFileUncheck(file: FileItem): void {
    // Find the file in the current folder contents and update its selected state
    const index = this.currentFolderContents.findIndex(item => item.id === file.id);
    if (index !== -1) {
      this.currentFolderContents[index].selected = false; // Uncheck the file
    }
  }

  // NEW APPROACH: Close sibling folders directly
  closeSiblingFoldersDirectly(folder: FileItem): void {
    console.log(`Closing sibling folders for: ${folder.name} (parent: ${folder.parentPath || 'root'})`);
    
    // First close sibling folders in the flattened items array
    this.flattenedItems.forEach(item => {
      if (item.type === 'folder' && 
          item.parentPath === folder.parentPath && 
          item.name !== folder.name && 
          item.expanded) {
        console.log(`Closing sibling folder in flattened items: ${item.name}`);
        item.expanded = false;
      }
    });

    // CRITICAL: Close sibling folders in the original data structure BEFORE updateFlattenedItems is called
    // This prevents them from being restored when the flattened view is rebuilt
    const closeSiblingsInSource = (items: FileItem[], currentPath: string): void => {
      if (!items || !Array.isArray(items)) return;
      
      items.forEach(item => {
        if (item.type === 'folder') {
          // Check if this folder is a sibling of our target folder
          if (currentPath === (folder.parentPath || '') && item.name !== folder.name && item.expanded) {
            console.log(`Closing sibling folder in original data: ${item.name} at path: ${currentPath}`);
            item.expanded = false;
          }
          
          // Recursively process children with updated path
          if (item.children && Array.isArray(item.children)) {
            const nextPath = currentPath ? `${currentPath}/${item.name}` : item.name;
            closeSiblingsInSource(item.children, nextPath);
          }
        }
      });
    };

    // Close siblings in the source data structure
    closeSiblingsInSource(this.clioFiles, '');
    
    // Force UI update
    this.updateItemVisibility();
    this.cdRef.detectChanges();
    
    console.log(`Completed closing sibling folders for: ${folder.name}`);
  }

  async toggleFolderSelectionDirect(folder: FileItem, checked: boolean): Promise<void> {
    console.log(`Direct folder selection for ${folder.name}: ${checked}`);

    // If the folder is being selected (checked = true) and it's not already expanded,
    // first expand it to load its children, then select them
    if (checked && !folder.expanded) {
      console.log(`Expanding folder ${folder.name} before selection`);
      
      // First expand the folder to load its children
      await this.toggleFolder(folder);
      
      // If the folder expansion failed or the folder has no children, don't proceed with selection
      if (!folder.expanded || !folder.children || folder.children.length === 0) {
        console.log(`Folder ${folder.name} could not be expanded or has no children, skipping selection`);
        folder.selected = false; // Reset the checkbox state
        return;
      }
    }

    // IMPORTANT: Set the selection state directly on the folder in the UI
    folder.selected = checked;

    // Find the actual folder in the source data structure and update it
    const sourceFolder = this.findFolderInSource(folder.name, folder.parentPath || '');
    if (sourceFolder) {
      sourceFolder.selected = checked;
    }

    // Also update the folder in the flattened items array
    const flattenedFolder = this.flattenedItems.find(
      item =>
        item.type === 'folder' && item.name === folder.name && (item.parentPath || '') === (folder.parentPath || ''),
    );

    if (flattenedFolder) {
      flattenedFolder.selected = checked;
    }

    // Process the selection/deselection
    if (checked) {
      // For folders, we don't add the folder itself to selectedFiles
      // Instead, we add all its child files directly
      if (folder.children) {
        this.addFolderChildrenToSelectedFiles(folder);
      }

      // Process children - add them to selectedFiles
      this.processChildrenSelection(folder, true);
    } else {
      // Remove all children of this folder from selectedFiles
      this.removeFolderChildrenFromSelectedFiles(folder);

      // Process children - remove them from selectedFiles
      this.processChildrenSelection(folder, false);
    }

    // Force a refresh of the UI
    setTimeout(() => {
      // Update the flattened items to reflect the changes in the UI
      this.updateFlattenedItems();
      this.updateItemVisibility();

      // Update duplicates state
      this.updateDuplicatesState();

      // Force Angular to detect changes
      this.refreshAllFolderReferences();
    }, 0);
  }
}

/**
 * Interface for transfer log items
 */
export interface TransferLogItem {
  name: string;
  status: 'success' | 'error';
  timestamp: Date;
  details?: string;
  parentFolder?: string;
  isNew?: boolean;
}
